import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, ViewChild, Injectable, InjectionToken, ViewChildren, ViewEncapsulation, Inject, HostListener, NgModule } from '@angular/core';
import * as i3 from '@engineering11/angular-email-editor';
import { EmailEditorComponent, EmailEditorModule } from '@engineering11/angular-email-editor';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i4 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { isTrue, isNotNil, pick, isDeepEqual } from '@engineering11/utility';
import { lastValueFrom, Subject, switchMap, withLatestFrom, mergeMap, from, tap, firstValueFrom, filter, of, map, BehaviorSubject, take, startWith, debounceTime, distinctUntilChanged, combineLatest, takeUntil } from 'rxjs';
import { filter as filter$1, map as map$1, distinctUntilChanged as distinctUntilChanged$1, switchMap as switchMap$1 } from 'rxjs/operators';
import * as i4$1 from '@engineering11/web-api-error';
import { e11Error } from '@engineering11/web-api-error';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { createEntityAdapter } from '@ngrx/entity';
import { CollectionRepository, FirestoreQueryBuilder, OrderByDirection, Pager } from '@engineering11/web-api-firebase';
import * as i3$1 from '@engineering11/web-ui-helpers';
import * as i1$1 from '@angular/platform-browser';
import * as i1$2 from '@angular/forms';
import { Validators, UntypedFormGroup, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i9$1 from '@engineering11/ui-lib/e11-form-builder';
import { e11FormFor, e11FormLayoutInlineGrid, e11FieldInputNumber, e11FieldInputSelect, e11FieldInputText, e11FormLayoutFullWidth, e11FieldInputSwitch, e11FieldInputCode, E11DynamicFormModule } from '@engineering11/ui-lib/e11-form-builder';
import { E11FormValidator } from '@engineering11/ui-lib/e11-input-errors';
import * as i7 from '@engineering11/ui-lib/e11-panel';
import { E11PanelModule } from '@engineering11/ui-lib/e11-panel';
import * as i8$1 from '@engineering11/ui-lib/e11-ui-utils';
import * as i10 from '@engineering11/ui-lib/e11-select';
import { E11SelectModule } from '@engineering11/ui-lib/e11-select';
import * as i11 from '@ngrx/component';
import { LetDirective } from '@ngrx/component';
import * as i12 from '@engineering11/ui-lib/e11-confirmation';
import { E11ConfirmationModule } from '@engineering11/ui-lib/e11-confirmation';
import { RestApiClient } from '@engineering11/web-api-rest';
import * as i2$1 from '@engineering11/ui-lib/e11-notifications';
import { E11NotificationsModule } from '@engineering11/ui-lib/e11-notifications';
import * as i3$2 from '@engineering11/tenant-bootstrap-web';
import * as i8 from '@engineering11/ui-lib/e11-loader-dots';
import { E11LoaderDotsModule } from '@engineering11/ui-lib/e11-loader-dots';
import * as i9 from '@engineering11/ui-lib/e11-modal';
import { E11ModalModule } from '@engineering11/ui-lib/e11-modal';
import { SearchEngine, SearchModule } from '@engineering11/search-web';
import * as i7$1 from '@engineering11/ui-lib/e11-input';
import { E11InputModule } from '@engineering11/ui-lib/e11-input';
import * as i2$2 from '@engineering11/web-utilities';
import { WebUtilityModule } from '@engineering11/web-utilities';
import * as i3$3 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i4$2 from '@engineering11/ui-lib/e11-badge';
import { E11BadgeModule } from '@engineering11/ui-lib/e11-badge';
import { E11TextareaModule } from '@engineering11/ui-lib/e11-textarea';

class HtmlEditorComponent {
    constructor() {
        this.minHeight = '600px';
        this.hideSaveButton = false;
        this.error = new EventEmitter();
        this.saved = new EventEmitter();
        this.editorLoaded = new EventEmitter();
        this.loading = new EventEmitter();
        this.options = {}; // See this for options: https://docs.unlayer.com/docs
    }
    ngOnInit() { }
    async handleEditorLoaded(event) {
        this.editorLoaded.emit(true);
    }
    // This should always be called in the event handler for editorLoaded (this.editorLoaded.emit(true)) on the consuming template
    loadDesign(design) {
        this.emailEditor.loadDesign(design);
    }
    async save() {
        this.loading.emit(true);
        try {
            const template = await new Promise((resolve, reject) => {
                this.emailEditor.exportHtml(async (exported) => {
                    if (exported.design && exported.html) {
                        const template = { design: exported.design, html: exported.html };
                        this.saved.emit(template);
                        resolve(template);
                    }
                    else {
                        reject(new Error('An unknown error occurred while exporting html'));
                    }
                });
            });
            this.loading.emit(false);
            return template;
        }
        catch (e) {
            this.loading.emit(false);
            this.error.emit(e);
        }
        throw new Error('An unknown error occurred');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: HtmlEditorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: HtmlEditorComponent, selector: "sdk-notifications-html-editor", inputs: { minHeight: "minHeight", displayMode: "displayMode", hideSaveButton: "hideSaveButton" }, outputs: { error: "error", saved: "saved", editorLoaded: "editorLoaded", loading: "loading" }, viewQueries: [{ propertyName: "emailEditor", first: true, predicate: EmailEditorComponent, descendants: true }], ngImport: i0, template: "<e11-button\n  *ngIf=\"!hideSaveButton\"\n  [icon]=\"'save'\"\n  [buttonGroup]=\"true\"\n  [iconPosition]=\"'left'\"\n  [iconSize]=\"'md-18'\"\n  [style]=\"'pill'\"\n  value=\"{{ 'Save' | translate }}\"\n  [color]=\"'primary-accent'\"\n  [type]=\"'button'\"\n  (click)=\"save()\"\n>\n</e11-button>\n\n<email-editor\n  id=\"editor1\"\n  [minHeight]=\"minHeight\"\n  [options]=\"options\"\n  [editorId]=\"'sdkHtmlEditor'\"\n  id=\"emailEditor\"\n  (loaded)=\"handleEditorLoaded($event)\"\n  #editor\n></email-editor>\n\n<!-- [projectId]=\"projectId\" -- displayMode -->\n", styles: [":host ::ng-deep email-editor iframe{min-width:100%!important;max-width:100%!important;width:100%!important;height:inherit!important}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i3.EmailEditorComponent, selector: "email-editor", inputs: ["editorId", "options", "projectId", "tools", "appearance", "locale", "id", "minHeight"], outputs: ["loaded", "ready"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: HtmlEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-notifications-html-editor', template: "<e11-button\n  *ngIf=\"!hideSaveButton\"\n  [icon]=\"'save'\"\n  [buttonGroup]=\"true\"\n  [iconPosition]=\"'left'\"\n  [iconSize]=\"'md-18'\"\n  [style]=\"'pill'\"\n  value=\"{{ 'Save' | translate }}\"\n  [color]=\"'primary-accent'\"\n  [type]=\"'button'\"\n  (click)=\"save()\"\n>\n</e11-button>\n\n<email-editor\n  id=\"editor1\"\n  [minHeight]=\"minHeight\"\n  [options]=\"options\"\n  [editorId]=\"'sdkHtmlEditor'\"\n  id=\"emailEditor\"\n  (loaded)=\"handleEditorLoaded($event)\"\n  #editor\n></email-editor>\n\n<!-- [projectId]=\"projectId\" -- displayMode -->\n", styles: [":host ::ng-deep email-editor iframe{min-width:100%!important;max-width:100%!important;width:100%!important;height:inherit!important}\n"] }]
        }], ctorParameters: () => [], propDecorators: { minHeight: [{
                type: Input
            }], displayMode: [{
                type: Input
            }], hideSaveButton: [{
                type: Input
            }], error: [{
                type: Output
            }], saved: [{
                type: Output
            }], editorLoaded: [{
                type: Output
            }], loading: [{
                type: Output
            }], emailEditor: [{
                type: ViewChild,
                args: [EmailEditorComponent]
            }] } });

class NotificationRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = `notifications/`;
    }
    buildId(notification) {
        const snakedName = notification.name.split(' ').join('_');
        return `${notification.group.toLocaleLowerCase()}_${snakedName.toLocaleLowerCase()}`;
    }
    async save(notification) {
        if (notification.id) {
            return this.update(notification);
        }
        else {
            const id = this.buildId(notification);
            const exists = await lastValueFrom(super.get(id));
            if (exists) {
                throw e11Error({ title: 'ID in use', message: `The specified ID is already in use ${id}`, type: 'notifications/id-in-use' });
            }
            notification.id = id;
            return this.add(notification);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class EmailTemplateRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = `notifications_email_template/`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EmailTemplateRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EmailTemplateRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EmailTemplateRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const notificationAdapter = createEntityAdapter({
    selectId: (notification) => notification.id,
});
const notificationSelectors = notificationAdapter.getSelectors();
const initialState$1 = notificationAdapter.getInitialState({
    notificationsLoaded: false,
    emailTemplates: null,
    emailTemplatesLoaded: false,
});
/**
 * Store to help manage notification configuration
 */
class NotificationConfigStore extends ComponentStore {
    constructor(notificationRepository, emailTemplateRepository, notificationHelper, errorHandler, logger) {
        super(initialState$1);
        this.notificationRepository = notificationRepository;
        this.emailTemplateRepository = emailTemplateRepository;
        this.notificationHelper = notificationHelper;
        this.errorHandler = errorHandler;
        this.logger = logger;
        this.saved = new Subject();
        this.deleted = new Subject();
        // SELECTORS
        this.getState = this.select((s) => s);
        this.notifications$ = this.select((s) => notificationSelectors.selectAll(s));
        this.notificationById$ = (id) => this.select((s) => notificationSelectors.selectEntities(s)[id]);
        this.selectedNotification$ = this.select((s) => s.selectedNotificationId ? notificationSelectors.selectEntities(s)[s.selectedNotificationId] : undefined);
        this.emailTemplates$ = this.select((s) => s.emailTemplates);
        this.emailTemplateOptions$ = this.select(this.emailTemplates$, (emailTemplates) => emailTemplates?.map((t) => ({ name: t.name, value: t.id })));
        this.selectedEmailTemplateId$ = this.select((s) => s.selectedEmailTemplateId);
        this.selectedEmailTemplate$ = this.select((s) => s.emailTemplates?.find((t) => t.id === s.selectedEmailTemplateId));
        this.notificationsLoaded$ = this.select((s) => s.notificationsLoaded);
        this.emailTemplatesLoaded$ = this.select((s) => s.emailTemplatesLoaded);
        this.getAllNotifications = this.effect((_$) => _$.pipe(switchMap((_) => {
            return this.notificationRepository.getAll().pipe(tapResponse((notifications) => this.onNotificationsFetched(notifications), (err) => this.errorHandler.handleError(err)));
        })));
        this.getAllEmailTemplates = this.effect((_$) => _$.pipe(switchMap((_) => {
            return this.emailTemplateRepository.getAll().pipe(tapResponse((templates) => this.setAllEmailTemplates(templates), (err) => this.errorHandler.handleError(err)));
        })));
        this.saveNotification = this.effect((saveRequest$) => saveRequest$.pipe(withLatestFrom(this.selectedEmailTemplateId$), mergeMap(([notification, templateId]) => {
            // Process self-referencing nudges before saving
            const processedNotification = this.processSelfReferencingNudges(notification);
            const notificationToSave = processedNotification.email && templateId
                ? { ...processedNotification, email: { ...processedNotification.email, templateId } }
                : processedNotification;
            this.logger.log('saveNotification', { notificationToSave });
            return from(this.notificationRepository.save(notificationToSave)).pipe(tapResponse((res) => {
                const savedNotification = { __createdAt: new Date(), __updatedAt: new Date(), id: res?.id, ...notificationToSave };
                this.notificationHelper.popNotification({ title: 'Saved', type: 'success', message: 'Notification Saved' });
                this.saved.next(savedNotification);
                return this.onNotificationSet(savedNotification);
            }, (err) => this.errorHandler.handleError(err, { alertUser: true })));
        })));
        this.selectNotification = this.effect((notificationId$) => notificationId$.pipe(tap((notificationId) => this.setSelectedNotificationId(notificationId)), tap(async (notificationId) => {
            if (!notificationId)
                return this.setSelectedEmailTemplateId(undefined);
            await firstValueFrom(this.notificationsLoaded$.pipe(filter(isTrue)));
            const notification = await firstValueFrom(this.notificationById$(notificationId));
            this.setSelectedEmailTemplateId(notification?.email?.templateId);
        })));
        this.deleteNotification = this.effect((notificationId$) => notificationId$.pipe(mergeMap((id) => from(this.notificationRepository.delete(id)).pipe(tapResponse((res) => {
            this.notificationHelper.popNotification({ title: 'Deleted', type: 'success', message: 'Notification Deleted' });
            this.deleted.next(true);
            return this.onNotificationDeleted(id);
        }, (err) => this.errorHandler.handleError(err, { alertUser: true }))))));
        // UPDATERS
        this.setSelectedNotificationId = this.updater((s, selectedNotificationId) => ({ ...s, selectedNotificationId }));
        this.setSelectedEmailTemplateId = this.updater((s, selectedEmailTemplateId) => ({ ...s, selectedEmailTemplateId }));
        this.onNotificationsFetched = this.updater((s, notifications) => notificationAdapter.setAll(notifications, { ...s, notificationsLoaded: true }));
        this.onNotificationSet = this.updater((s, notification) => notificationAdapter.setOne(notification, s));
        this.onNotificationDeleted = this.updater((s, id) => notificationAdapter.removeOne(id, s));
        this.setAllEmailTemplates = this.updater((s, emailTemplates) => ({
            ...s,
            emailTemplatesLoaded: true,
            emailTemplates,
        }));
    }
    // EFFECTS
    async getNotificationsIfNotLoaded() {
        const loaded = await firstValueFrom(this.notificationsLoaded$);
        if (!loaded)
            this.getAllNotifications();
    }
    async getEmailTemplatesIfNotLoaded() {
        const loaded = await firstValueFrom(this.emailTemplatesLoaded$);
        if (!loaded)
            this.getAllEmailTemplates();
    }
    // PRIVATE HELPER METHODS
    /**
     * Processes self-referencing nudges by replacing __SELF__ with the actual notification ID
     */
    processSelfReferencingNudges(notification) {
        if (!notification.nudges?.length) {
            return notification;
        }
        // Check if any nudges reference __SELF__
        const hasSelfReferencingNudges = notification.nudges.some((nudge) => nudge.notificationId === '__SELF__');
        if (!hasSelfReferencingNudges) {
            return notification; // No self-references, return as-is
        }
        // Generate the ID that will be used for this notification
        // Only generate ID if this is a new notification (no existing ID)
        const notificationId = notification.id || this.generateNotificationId(notification.group, notification.name);
        // Replace __SELF__ with the predicted notification ID
        const processedNudges = notification.nudges.map((nudge) => {
            if (nudge.notificationId === '__SELF__') {
                return { ...nudge, notificationId: notificationId };
            }
            return nudge;
        });
        // DON'T set the ID on the main notification for new notifications
        // Let the backend generate and return the real ID
        return {
            ...notification,
            // Only set ID if it already existed (editing case)
            ...(notification.id && { id: notification.id }),
            nudges: processedNudges,
        };
    }
    /**
     * Generates a notification ID from group and name
     * Based on the interface comment: "generated by group and name" with example "auth_reset_password"
     */
    generateNotificationId(group, name) {
        const cleanGroup = group.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
        return `${cleanGroup}_${cleanName}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationConfigStore, deps: [{ token: NotificationRepository }, { token: EmailTemplateRepository }, { token: i3$1.NotificationHelper }, { token: i4$1.E11ErrorHandlerService }, { token: i4$1.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationConfigStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationConfigStore, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: NotificationRepository }, { type: EmailTemplateRepository }, { type: i3$1.NotificationHelper }, { type: i4$1.E11ErrorHandlerService }, { type: i4$1.E11Logger }] });

const EmailNotificationReplaceTokens = {
    messageText: '{{messageText}}',
    callToActionText: '{{callToActionText}}',
};

class HtmlPreviewService {
    constructor(_sanitizer) {
        this._sanitizer = _sanitizer;
    }
    makeHtmlPreview(htmlTemplate, substitutions) {
        const callToActionText = substitutions?.callToActionText;
        const messageText = substitutions?.text;
        let html = htmlTemplate.slice();
        if (callToActionText) {
            html = html.replace(EmailNotificationReplaceTokens.callToActionText, callToActionText);
        }
        if (messageText) {
            html = html.replace(EmailNotificationReplaceTokens.messageText, messageText);
        }
        return this._sanitizer.bypassSecurityTrustHtml(html);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: HtmlPreviewService, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: HtmlPreviewService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: HtmlPreviewService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.DomSanitizer }] });

class EditNotificationForm {
    constructor() { }
    buildForms() {
        const general = this.buildGeneralForm();
        const email = this.buildEmailForm();
        const push = this.buildPushForm();
        const inApp = this.buildInAppForm();
        return { general, email, push, inApp };
    }
    buildNudgesForm(notificationList, defaultValue) {
        return e11FormFor()([
            e11FormLayoutInlineGrid([
                e11FieldInputNumber('offset', 'Delay (days)', defaultValue?.offset ?? 0, {
                    validatorOrOpts: [Validators.required, Validators.min(0), Validators.max(365)],
                    extraInputOption: { tooltipConfig: { tooltipText: 'The number of days before the nudge is sent.' } },
                }),
                e11FieldInputSelect('notificationId', 'Notification', notificationList, defaultValue?.notificationId ?? undefined, {
                    validatorOrOpts: [Validators.required],
                    extraInputOption: { tooltipConfig: { tooltipText: 'The notification to send as a nudge.' } },
                }),
            ], {
                containerClass: 'e11-w-full e11-flex-row-reverse e11-items-center',
                fieldsClass: '',
                templateSpec: {
                    templateKey: 'removeNudge',
                    templatePosition: 'left',
                },
            }),
        ]);
    }
    buildGeneralForm() {
        return e11FormFor()([
            e11FormLayoutInlineGrid([
                e11FieldInputText('group', 'Group', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(15), E11FormValidator.nameValidator()],
                    extraInputOption: { tooltipConfig: { tooltipText: 'The "audience" or "sdk" where possible.' } },
                }),
                e11FieldInputText('name', 'Name', undefined, {
                    validatorOrOpts: [Validators.required, Validators.maxLength(40), Validators.minLength(5), E11FormValidator.nameValidator()],
                    extraInputOption: { tooltipConfig: { tooltipText: 'The event that happened. Past tense where possible.' } },
                }),
            ]),
            e11FormLayoutFullWidth([
                e11FieldInputText('sentWhen', 'Sent When', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(5), Validators.maxLength(70)],
                    extraInputOption: { tooltipConfig: { tooltipText: 'A user-friendly explanation of what triggers the notification.' } },
                }),
                e11FieldInputText('sendThresholdMilliseconds', 'Default sendThresholdMilliseconds', undefined, {
                    extraInputOption: { tooltipConfig: { tooltipText: 'If none is specified, defaults to 60s' } },
                }),
            ]),
        ]);
    }
    buildEmailForm() {
        return e11FormFor()([
            e11FormLayoutInlineGrid([e11FieldInputSwitch('notificationOn', 'On', false)]),
            e11FormLayoutInlineGrid([
                e11FieldInputText('fromTitle', 'From Title', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(20)],
                }),
                e11FieldInputText('subject', 'Subject', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(50)],
                }),
            ]),
            e11FormLayoutInlineGrid([
                e11FieldInputText('callToActionText', 'Call To Action Text', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(40)],
                }),
                e11FieldInputText('sendThresholdMilliseconds', 'sendThresholdMilliseconds', undefined, {}),
            ]),
            e11FormLayoutInlineGrid([e11FieldInputCode('text', 'Email Text', undefined, {})]),
        ]);
    }
    buildPushForm() {
        return this.buildInAppForm();
    }
    buildInAppForm() {
        return e11FormFor()([
            e11FormLayoutInlineGrid([e11FieldInputSwitch('notificationOn', 'On', false)]),
            e11FormLayoutInlineGrid([
                e11FieldInputText('subject', 'Subject', undefined, {
                    validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(140)],
                }),
                e11FieldInputText('sendThresholdMilliseconds', 'sendThresholdMilliseconds', undefined, {}),
            ]),
            e11FormLayoutInlineGrid([
                e11FieldInputText('text', 'Text', undefined, { validatorOrOpts: [Validators.required, Validators.minLength(4), Validators.maxLength(140)] }),
            ]),
        ]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditNotificationForm, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditNotificationForm, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditNotificationForm, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

const APP_NOTIFICATION_HANDLER_TOKEN = new InjectionToken('APP_NOTIFICATION_HANDLER_TOKEN');
const UNREAD_NOTIFICATIONS_LIMIT = 9; // The limit to the count we display
const UNREAD_NOTIFICATIONS_PAGE_SIZE = UNREAD_NOTIFICATIONS_LIMIT + 1; // We need to fetch one more than the display limit so we can append a + if we have "more than LIMIT"
const NOTIFICATIONS_DROPDOWN_COUNT = 5;
const DEFAULT_PAGE_SIZE = NOTIFICATIONS_DROPDOWN_COUNT; // MUST be greater than or equal to NOTIFICATIONS_DROPDOWN_COUNT
const ERROR_TYPES = Object.freeze({
    NOTIFICATION_TO_VM: 'notification/to-view-model',
    MISSING_CONFIGURATION: 'notifications/missing-configuration',
});
const LOGO_IMAGE_PLACEHOLDER = 'https://content.engineering11.com/assets/logos/generic-email-logo-placeholder.png';
var NotificationPermissions;
(function (NotificationPermissions) {
    NotificationPermissions["TestNotification"] = "notifications/platform-admin-test-notification";
    NotificationPermissions["ViewNotifications"] = "notifications/platform-admin-view-notifications";
    NotificationPermissions["EditNotifications"] = "notifications/platform-admin-edit-notifications";
})(NotificationPermissions || (NotificationPermissions = {}));

class BaseNotificationConfigProvider {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseNotificationConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseNotificationConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseNotificationConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });
class DefaultLibConfiguration extends BaseNotificationConfigProvider {
    constructor(logger) {
        super();
        this.logger = logger;
    }
    get config() {
        this.logger.warn('WARNING - wire up NotificationConfigurationProvider in your app');
        // return default config
        return {
            userId: of(),
            notificationsBaseURL: '',
            token$: of(),
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DefaultLibConfiguration, deps: [{ token: i4$1.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DefaultLibConfiguration, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: DefaultLibConfiguration, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i4$1.E11Logger }] });

class NotificationTestingService {
    constructor(configProvider) {
        this.configProvider = configProvider;
        if (!this.configProvider.config.notificationsBaseURL) {
            throw e11Error({
                type: ERROR_TYPES.MISSING_CONFIGURATION,
                title: 'Missing configuration',
                message: 'Missing configuration, notificationsBaseURL is undefined.',
            });
        }
        this.restClient = new RestApiClient({
            baseUrl: this.configProvider.config.notificationsBaseURL,
            token: this.configProvider.config.token$.pipe(filter((token) => isNotNil(token))),
            throwNon200s: true,
        });
    }
    async requestTestNotification(notificationTestRequest) {
        return lastValueFrom(this.restClient
            .post('notification-test', notificationTestRequest, { version: '1' })
            .pipe(map((response) => response.data)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationTestingService, deps: [{ token: BaseNotificationConfigProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationTestingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationTestingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: BaseNotificationConfigProvider }] });

const TOKEN_NAME = 'token';
class TokenStorage {
    constructor() {
        // JWT { }
        this.storageSub = new BehaviorSubject('');
    }
    /**
     * watchStorage for changes
     */
    watchStorage() {
        return this.storageSub.asObservable();
    }
    /**
     *
     *
     * @param {string} key
     * @param {*} data
     * @memberof TokenStorage
     */
    setItem(key, data) {
        localStorage.setItem(key, data);
        this.storageSub.next('changed!');
    }
    getItem(key) {
        const item = localStorage.getItem(key);
        if (item) {
            return JSON.parse(item);
        }
        return null;
    }
    /**
     *
     *
     * @param {string} key
     * @memberof TokenStorage
     */
    removeItem(key) {
        localStorage.removeItem(key);
        this.storageSub.next('changed!');
    }
    /**
  
     *
     *
     * @returns {Observable<string>}
     * @memberof TokenStorage
     */
    getAccessToken() {
        const token = localStorage.getItem(TOKEN_NAME);
        return of(token);
    }
    /**
     *
     *
     * @param {string} token
     * @returns {TokenStorage}
     * @memberof TokenStorage
     */
    setAccessToken(token) {
        localStorage.setItem(TOKEN_NAME, token);
        return this;
    }
    /**
     *
     *
     * @param {string} token
     * @returns {TokenStorage}
     * @memberof TokenStorage
     */
    setRefreshToken(token) {
        localStorage.setItem('refreshToken', token);
        return this;
    }
    /**
     *
     *
     * @memberof TokenStorage
     */
    clear() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TokenStorage, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TokenStorage, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: TokenStorage, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class NotificationTestComponent {
    constructor(notificationTestingService, uiNotificationService, tenantBootstrapRepository, tokenStorage) {
        this.notificationTestingService = notificationTestingService;
        this.uiNotificationService = uiNotificationService;
        this.tenantBootstrapRepository = tenantBootstrapRepository;
        this.tokenStorage = tokenStorage;
        this.loading = false;
        this.tenantSelectOptions = [];
        this.notificationVariables = [];
    }
    ngOnInit() {
        this.tenantBootstrapRepository
            .getAll()
            .pipe(take(1))
            .subscribe((tenants) => {
            tenants.forEach((t) => {
                this.tenantSelectOptions?.push({ name: t.name, value: t.customerKey });
            });
        });
    }
    open() {
        this.modalTestRequest?.open();
    }
    cancel() {
        this.modalTestRequest?.close();
    }
    addVariable() {
        this.notificationVariables.push({ key: '', value: '' });
    }
    removeVariable(idx) {
        this.notificationVariables.splice(idx, 1);
    }
    selectTenant(customerKey) {
        this.customerTenantId = customerKey;
    }
    async runTest() {
        if (!this.customerTenantId) {
            this.sendNotification('Tenant Selection', 'A tenant must be selected', 'warning');
            return;
        }
        this.loading = true;
        const data = this.buildDataJson();
        const user = this.tokenStorage.getItem('user');
        const payload = {
            toEmail: user.email,
            data: data,
            notificationId: this.notificationId,
            tenantCustomerKey: this.customerTenantId,
        };
        await this.notificationTestingService.requestTestNotification(payload);
        this.loading = false;
        this.modalTestRequest.close();
        this.sendNotification('Test Notification Sent', `The test notification has been sent to ${user.email}`, 'success');
    }
    buildDataJson() {
        const data = {};
        this.notificationVariables.forEach((v) => (data[v.key] = v.value));
        return data;
    }
    sendNotification(title, message, type) {
        const notification = {
            title: title,
            message: message,
            type: type,
            autoClose: true,
            dismissOnRouteChange: true,
        };
        this.uiNotificationService.popNotificationMessage(notification);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationTestComponent, deps: [{ token: NotificationTestingService }, { token: i2$1.E11NotificationsService }, { token: i3$2.TenantBootstrapRepository }, { token: TokenStorage }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: NotificationTestComponent, selector: "notification-test", inputs: { notificationId: "notificationId" }, viewQueries: [{ propertyName: "modalTestRequest", first: true, predicate: ["modalTestRequest"], descendants: true }], ngImport: i0, template: "<e11-modal #modalTestRequest [width]=\"'lg'\" [title]=\"'Test Notification' | translate\" [showHeader]=\"true\">\n  <div modal-body>\n    <div class=\"e11-mb-7\">\n      <e11-select *ngIf=\"tenantSelectOptions\" (valueChange)=\"selectTenant($event)\" [options]=\"tenantSelectOptions\" label=\"Select Tenant\"></e11-select>\n    </div>\n\n    <div class=\"e11-w-full e11-flex e11-mb-2\">\n      <div class=\"e11-flex e11-grow e11-items-end\">\n        <p class=\"e11-mb-0\">{{ \"Variables\" | translate }}</p>\n      </div>\n      <div class=\"e11-elex e11-justify-end\"></div>\n    </div>\n\n    <section class=\"e11-mb-8\">\n      <table class=\"e11-w-full\">\n        <thead>\n          <tr class=\"e11-border-b e11-border-skin-grey/50\">\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-left e11-w-1/3\">{{ \"Key\" | translate }}</th>\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-left e11-w-1/3\">{{ \"Value\" | translate }}</th>\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-right e11-w-1/3\">\n              <e11-button\n                [icon]=\"'add'\"\n                [iconPosition]=\"'left'\"\n                [iconSize]=\"'md-18'\"\n                [size]=\"'sm'\"\n                [color]=\"'primary-accent'\"\n                [value]=\"'Add variable' | translate\"\n                (click)=\"addVariable()\"\n              ></e11-button>\n            </th>\n          </tr>\n        </thead>\n        <tbody class=\"\">\n          <tr class=\"even:e11-bg-skin-grey/10 e11-py-2 e11-h-12\" *ngFor=\"let variable of notificationVariables; let i = index\">\n            <td class=\"e11-w-1/3 e11-h-12 e11-py-2 e11-pr-2\">\n              <input\n                class=\"e11-border e11-border-skin-grey/50 e11-rounded e11-p-1 e11-w-full\"\n                [(ngModel)]=\"variable.key\"\n                value=\"{{ variable.key }}\"\n              />\n            </td>\n            <td class=\"e11-w-1/3 e11-h-12 e11-py-2 e11-pr-2\">\n              <input\n                class=\"e11-border e11-border-skin-grey/50 e11-rounded e11-p-1 e11-w-full\"\n                [(ngModel)]=\"variable.value\"\n                value=\"{{ variable.value }}\"\n              />\n            </td>\n            <td class=\"e11-w-1/3 e11-text-right e11-h-12 e11-py-2\">\n              <e11-button\n                [icon]=\"'close'\"\n                [ghost]=\"true\"\n                [iconPosition]=\"'left'\"\n                [iconSize]=\"'md-14'\"\n                [size]=\"'sm'\"\n                [color]=\"'secondary'\"\n                [value]=\"'Delete' | translate\"\n                (click)=\"removeVariable(i)\"\n              ></e11-button>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n      <div\n        class=\"e11-w-full e11-flex e11-items-center e11-bg-skin-grey/10 e11-h-12 e11-p-2 e11-text-sm e11-text-skin-light\"\n        *ngIf=\"notificationVariables.length === 0\"\n      >\n        {{ \"No variables\" | translate }}\n      </div>\n    </section>\n\n    <e11-loader-dots *ngIf=\"loading\"></e11-loader-dots>\n\n    <div class=\"e11-flex e11-items-center e11-justify-end\">\n      <a class=\"e11-cancel-link e11-mr-4 e11-cursor-pointer e11-text-skin-light hover:e11-text-skin-primary\" (click)=\"cancel()\">\n        {{ \"Cancel\" | translate }}</a\n      >\n      <e11-button (click)=\"runTest()\" [color]=\"'primary-accent'\" [value]=\"'Test' | translate\" [disabled]=\"loading\"></e11-button>\n    </div>\n  </div>\n</e11-modal>\n", styles: [":host ::ng-deep json-editor,:host ::ng-deep json-editor .jsoneditor,:host ::ng-deep json-editor>div,:host ::ng-deep json-editor jsoneditor-outer{height:500px}:host ::ng-deep json-editor jsoneditor-inner{height:100px}:host ::ng-deep json-editor textarea.jsoneditor-text{height:200px;width:200px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i8.E11LoaderDotsComponent, selector: "e11-loader-dots", inputs: ["color", "size", "fontSize", "fontColor", "label", "buttonLoader", "fade", "fullScreen", "active", "hasBackdrop", "backdropStyle"] }, { kind: "component", type: i9.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "component", type: i10.E11SelectComponent, selector: "e11-select", inputs: ["id", "name", "label", "size", "selectMessage", "labelClass", "inputClass", "containerClass", "parentForm", "formControlName", "options", "value", "hasError", "required", "disabled", "loading", "tooltipConfig", "labelTextOptional", "selectClass"], outputs: ["valueChange", "onValueChange"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationTestComponent, decorators: [{
            type: Component,
            args: [{ selector: 'notification-test', template: "<e11-modal #modalTestRequest [width]=\"'lg'\" [title]=\"'Test Notification' | translate\" [showHeader]=\"true\">\n  <div modal-body>\n    <div class=\"e11-mb-7\">\n      <e11-select *ngIf=\"tenantSelectOptions\" (valueChange)=\"selectTenant($event)\" [options]=\"tenantSelectOptions\" label=\"Select Tenant\"></e11-select>\n    </div>\n\n    <div class=\"e11-w-full e11-flex e11-mb-2\">\n      <div class=\"e11-flex e11-grow e11-items-end\">\n        <p class=\"e11-mb-0\">{{ \"Variables\" | translate }}</p>\n      </div>\n      <div class=\"e11-elex e11-justify-end\"></div>\n    </div>\n\n    <section class=\"e11-mb-8\">\n      <table class=\"e11-w-full\">\n        <thead>\n          <tr class=\"e11-border-b e11-border-skin-grey/50\">\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-left e11-w-1/3\">{{ \"Key\" | translate }}</th>\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-left e11-w-1/3\">{{ \"Value\" | translate }}</th>\n            <th scope=\"col\" class=\"e11-text-sm e11-font-bold e11-text-right e11-w-1/3\">\n              <e11-button\n                [icon]=\"'add'\"\n                [iconPosition]=\"'left'\"\n                [iconSize]=\"'md-18'\"\n                [size]=\"'sm'\"\n                [color]=\"'primary-accent'\"\n                [value]=\"'Add variable' | translate\"\n                (click)=\"addVariable()\"\n              ></e11-button>\n            </th>\n          </tr>\n        </thead>\n        <tbody class=\"\">\n          <tr class=\"even:e11-bg-skin-grey/10 e11-py-2 e11-h-12\" *ngFor=\"let variable of notificationVariables; let i = index\">\n            <td class=\"e11-w-1/3 e11-h-12 e11-py-2 e11-pr-2\">\n              <input\n                class=\"e11-border e11-border-skin-grey/50 e11-rounded e11-p-1 e11-w-full\"\n                [(ngModel)]=\"variable.key\"\n                value=\"{{ variable.key }}\"\n              />\n            </td>\n            <td class=\"e11-w-1/3 e11-h-12 e11-py-2 e11-pr-2\">\n              <input\n                class=\"e11-border e11-border-skin-grey/50 e11-rounded e11-p-1 e11-w-full\"\n                [(ngModel)]=\"variable.value\"\n                value=\"{{ variable.value }}\"\n              />\n            </td>\n            <td class=\"e11-w-1/3 e11-text-right e11-h-12 e11-py-2\">\n              <e11-button\n                [icon]=\"'close'\"\n                [ghost]=\"true\"\n                [iconPosition]=\"'left'\"\n                [iconSize]=\"'md-14'\"\n                [size]=\"'sm'\"\n                [color]=\"'secondary'\"\n                [value]=\"'Delete' | translate\"\n                (click)=\"removeVariable(i)\"\n              ></e11-button>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n      <div\n        class=\"e11-w-full e11-flex e11-items-center e11-bg-skin-grey/10 e11-h-12 e11-p-2 e11-text-sm e11-text-skin-light\"\n        *ngIf=\"notificationVariables.length === 0\"\n      >\n        {{ \"No variables\" | translate }}\n      </div>\n    </section>\n\n    <e11-loader-dots *ngIf=\"loading\"></e11-loader-dots>\n\n    <div class=\"e11-flex e11-items-center e11-justify-end\">\n      <a class=\"e11-cancel-link e11-mr-4 e11-cursor-pointer e11-text-skin-light hover:e11-text-skin-primary\" (click)=\"cancel()\">\n        {{ \"Cancel\" | translate }}</a\n      >\n      <e11-button (click)=\"runTest()\" [color]=\"'primary-accent'\" [value]=\"'Test' | translate\" [disabled]=\"loading\"></e11-button>\n    </div>\n  </div>\n</e11-modal>\n", styles: [":host ::ng-deep json-editor,:host ::ng-deep json-editor .jsoneditor,:host ::ng-deep json-editor>div,:host ::ng-deep json-editor jsoneditor-outer{height:500px}:host ::ng-deep json-editor jsoneditor-inner{height:100px}:host ::ng-deep json-editor textarea.jsoneditor-text{height:200px;width:200px}\n"] }]
        }], ctorParameters: () => [{ type: NotificationTestingService }, { type: i2$1.E11NotificationsService }, { type: i3$2.TenantBootstrapRepository }, { type: TokenStorage }], propDecorators: { notificationId: [{
                type: Input
            }], modalTestRequest: [{
                type: ViewChild,
                args: ['modalTestRequest']
            }] } });

class EditNotificationComponent {
    set notificationId(notificationId) {
        this._notificationId = notificationId;
        this.notificationConfigStore.selectNotification(notificationId);
    }
    constructor(logger, notificationConfigStore, htmlPreviewService, editNotificationForm) {
        this.logger = logger;
        this.notificationConfigStore = notificationConfigStore;
        this.htmlPreviewService = htmlPreviewService;
        this.editNotificationForm = editNotificationForm;
        this.nudgesFieldGroups = [];
        this.notificationForms = this.editNotificationForm.buildForms();
        this.selectedNotification$ = this.notificationConfigStore.selectedNotification$;
        this.selectedEmailTemplate$ = this.notificationConfigStore.selectedEmailTemplate$;
        this.emailTemplatesLoaded$ = this.notificationConfigStore.emailTemplatesLoaded$;
        this.notificationsLoaded$ = this.notificationConfigStore.notificationsLoaded$;
        this.emailTemplates$ = this.notificationConfigStore.emailTemplateOptions$;
        this.notifications$ = this.notificationConfigStore.notifications$;
        this.htmlPreview$ = of(undefined);
        this.editCancelled = new EventEmitter();
        this.notificationDeleted = this.notificationConfigStore.deleted;
        this.notificationSaved = this.notificationConfigStore.saved;
    }
    async ngOnInit() {
        this.notificationConfigStore.getNotificationsIfNotLoaded();
        this.notificationConfigStore.getEmailTemplatesIfNotLoaded();
        await this.loadNudges();
    }
    ngAfterViewInit() {
        // Must wait until forms are initialised before we can build the preview
        this.htmlPreview$ = this.buildHtmlPreview();
    }
    async addNewNudge() {
        const notificationList = await this.getNotificationListWithCurrent();
        const selectedNotification = await firstValueFrom(this.selectedNotification$);
        const defaultNotificationId = selectedNotification?.id || '__SELF__';
        const defaultValue = { offset: 7, notificationId: defaultNotificationId };
        const newNudgeForm = this.editNotificationForm.buildNudgesForm(notificationList, defaultValue);
        this.nudgesFieldGroups.push(newNudgeForm);
    }
    removeNudge(index) {
        this.nudgesFieldGroups.splice(index, 1);
    }
    onSelectValueChange(templateId) {
        this.notificationConfigStore.setSelectedEmailTemplateId(templateId);
    }
    async loadNudges() {
        const selectedNotificationNudges = (await firstValueFrom(this.selectedNotification$.pipe(filter$1(isNotNil))))?.nudges ?? [];
        if (!selectedNotificationNudges.length)
            return;
        const notificationList = await this.getNotificationListWithCurrent();
        const fieldGroups = selectedNotificationNudges.map((nudge) => {
            const duration = nudge.offset ?? 0;
            return this.editNotificationForm.buildNudgesForm(notificationList, { ...nudge, offset: duration });
        });
        this.nudgesFieldGroups.push(...fieldGroups);
    }
    async getNotificationList() {
        return (await firstValueFrom(this.notifications$))
            .map((notification) => {
            return { name: `${notification.group} - ${notification.name}`, value: notification.id };
        })
            .sort((a, b) => a.name.localeCompare(b.name));
    }
    async getNotificationListWithCurrent() {
        const existingNotifications = await this.getNotificationList();
        const selfReferenceOption = {
            name: 'This Notification (Self)',
            value: '__SELF__',
        };
        return [selfReferenceOption, ...existingNotifications];
    }
    async save() {
        if (this.checkFormInvalid())
            return;
        const general = this.generalForm.form.value;
        const email = this.emailForm.form.value;
        const push = this.pushForm.form.value;
        const inApp = this.inAppForm.form.value;
        const nudges = this.notificationNudgesFormBuilders.map((nudgeForm) => {
            return { ...nudgeForm.form.value, offset: +nudgeForm.form.value.offset };
        });
        const notification = {
            id: this._notificationId,
            ...general,
            email: { ...email, templateId: '' },
            inApp,
            push,
            nudges,
        };
        this.notificationConfigStore.saveNotification(notification);
    }
    async deleteNotification(answer) {
        if (!answer || !this._notificationId)
            return;
        this.notificationConfigStore.deleteNotification(this._notificationId);
    }
    async cancel() {
        this.editCancelled.emit();
    }
    async testNotification() {
        this.notificationTest?.open();
    }
    /**
     * Must be run after the form is initialised
     */
    buildHtmlPreview() {
        const formValues$ = this.emailForm.form.valueChanges.pipe(startWith(this.emailForm.form.value), debounceTime(70), map((v) => pick(v, 'callToActionText', 'text')), distinctUntilChanged(isDeepEqual));
        return combineLatest([this.selectedNotification$, this.selectedEmailTemplate$, formValues$]).pipe(map(([notification, template, values]) => {
            if (!template)
                return undefined;
            this.logger.log('buildHtmlPreview', { notification, template, values });
            return this.htmlPreviewService.makeHtmlPreview(template.editorOutput.html, { ...notification?.email, ...values });
        }));
    }
    /** Performs a validity check on the form. Triggers error handling if necessary. Returns true if the form is invalid */
    checkFormInvalid() {
        const emailOn = this.emailForm.form.value.notificationOn;
        const pushOn = this.pushForm.form.value.notificationOn;
        const inAppOn = this.inAppForm.form.value.notificationOn;
        this.generalForm.onSubmitClick();
        if (emailOn)
            this.emailForm.onSubmitClick();
        if (pushOn)
            this.pushForm.onSubmitClick();
        if (inAppOn)
            this.inAppForm.onSubmitClick();
        this.notificationNudgesFormBuilders.map((nudgeForm) => nudgeForm.onSubmitClick());
        const formIsInvalid = this.generalForm.form.invalid ||
            (this.emailForm.form.invalid && emailOn) ||
            (this.pushForm.form.invalid && pushOn) ||
            (this.inAppForm.form.invalid && inAppOn) ||
            !!this.notificationNudgesFormBuilders.find((nudgeForm) => nudgeForm.form.invalid);
        if (formIsInvalid)
            this.logger.log('Form Invalid', {
                general: this.generalForm,
                nudge: this.notificationNudgesFormBuilders,
                email: this.emailForm,
                push: this.pushForm,
                inApp: this.inAppForm,
            });
        return formIsInvalid;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditNotificationComponent, deps: [{ token: i4$1.E11Logger }, { token: NotificationConfigStore }, { token: HtmlPreviewService }, { token: EditNotificationForm }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: EditNotificationComponent, selector: "sdk-edit-notification", inputs: { notificationId: "notificationId" }, outputs: { editCancelled: "editCancelled", notificationDeleted: "notificationDeleted", notificationSaved: "notificationSaved" }, viewQueries: [{ propertyName: "generalForm", first: true, predicate: ["generalForm"], descendants: true }, { propertyName: "emailForm", first: true, predicate: ["emailForm"], descendants: true }, { propertyName: "pushForm", first: true, predicate: ["pushForm"], descendants: true }, { propertyName: "inAppForm", first: true, predicate: ["inAppForm"], descendants: true }, { propertyName: "notificationTest", first: true, predicate: ["notificationTest"], descendants: true }, { propertyName: "confirmDelete", first: true, predicate: ["confirmDelete"], descendants: true }, { propertyName: "notificationNudgesFormBuilders", predicate: ["notificationNudgesFormBuilder"], descendants: true }], ngImport: i0, template: "<h2 class=\"e11-text-skin-primary-accent e11-mb-4\">{{ \"Edit Notification\" | translate }}</h2>\n<div class=\"e11-flex e11-flex-col lg:e11-flex-row e11-gap-6\">\n  <div class=\"e11-w-full lg:e11-w-1/2\">\n    <ng-container *ngrxLet=\"emailTemplatesLoaded$ as emailTemplatesLoaded\">\n      <ng-container *ngrxLet=\"htmlPreview$ as htmlPreview\">\n        <ng-container *ngrxLet=\"emailTemplates$ as emailTemplates\">\n          <div>\n            <e11-select\n              [loading]=\"!emailTemplatesLoaded\"\n              [options]=\"emailTemplates ?? []\"\n              (valueChange)=\"onSelectValueChange($event)\"\n              label=\"Select an Email Template\"\n            ></e11-select>\n          </div>\n          <div *ngIf=\"htmlPreview\">\n            <e11-panel>\n              <h3 class=\"e11-mb-4\">{{ \"Email Preview\" | translate }}</h3>\n\n              <div class=\"e11-w-full\">\n                <div class=\"e11-pb-4\" [innerHTML]=\"htmlPreview\"></div>\n              </div>\n            </e11-panel>\n          </div>\n        </ng-container>\n      </ng-container>\n    </ng-container>\n  </div>\n  <div class=\"e11-w-full lg:e11-w-1/2\">\n    <ng-container *ngrxLet=\"notificationsLoaded$ as notificationsLoaded\">\n      <ng-container *ngrxLet=\"selectedNotification$ as selectedNotification\">\n        <e11-panel>\n          <div class=\"e11-w-full e11-flex\">\n            <div class=\"e11-grow\">\n              <h3 class=\"e11-mb-4\">{{ \"Configuration\" | translate }}</h3>\n            </div>\n            <div class=\"e11-flex e11-justify-end\">\n              <e11-button\n                [size]=\"'sm'\"\n                [color]=\"'primary-accent'\"\n                [value]=\"'Send a test notification' | translate\"\n                [skeletonLoading]=\"!notificationsLoaded\"\n                (click)=\"testNotification()\"\n              ></e11-button>\n            </div>\n          </div>\n\n          <notification-test #notificationTest [notificationId]=\"_notificationId\"></notification-test>\n\n          <div class=\"e11-w-full\">\n            <e11-dynamic-form-builder\n              #generalForm\n              [initialValue]=\"selectedNotification\"\n              [fieldGroups]=\"notificationForms.general\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <!--  nudges  -->\n            <ng-container *ngFor=\"let nudgeFieldGroups of nudgesFieldGroups; let i = index\">\n              <e11-dynamic-form-builder #notificationNudgesFormBuilder [initialValue]=\"{}\" [hideSubmitMenu]=\"true\" [fieldGroups]=\"nudgeFieldGroups\">\n                <ng-template e11Template=\"removeNudge\">\n                  <e11-button\n                    [value]=\"''\"\n                    [color]=\"'red'\"\n                    [size]=\"'sm'\"\n                    [icon]=\"'delete_forever'\"\n                    [iconClassOverrides]=\"'-e11-mr-1'\"\n                    (click)=\"removeNudge(i)\"\n                  ></e11-button>\n                </ng-template>\n              </e11-dynamic-form-builder>\n            </ng-container>\n            <e11-button\n              [disabled]=\"!notificationsLoaded\"\n              [value]=\"'Add Nudge' | translate\"\n              [color]=\"'primary-accent'\"\n              [size]=\"'sm'\"\n              (click)=\"addNewNudge()\"\n            ></e11-button>\n            <div class=\"e11-mb-8\"></div>\n\n            <h3 class=\"e11-mb-4\">{{ \"Email\" | translate }}</h3>\n            <e11-dynamic-form-builder\n              #emailForm\n              [initialValue]=\"selectedNotification?.email\"\n              [fieldGroups]=\"notificationForms.email\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <h3 class=\"e11-mb-4\">{{ \"Push\" }}</h3>\n            <e11-dynamic-form-builder\n              #pushForm\n              [initialValue]=\"selectedNotification?.push\"\n              [fieldGroups]=\"notificationForms.push\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <h3 class=\"e11-mb-4\">{{ \"In-App\" }}</h3>\n            <e11-dynamic-form-builder\n              #inAppForm\n              [initialValue]=\"selectedNotification?.inApp\"\n              [fieldGroups]=\"notificationForms.inApp\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n          </div>\n          <div class=\"e11-flex\">\n            <div class=\"e11-ml-auto\">\n              <e11-button value=\"{{ 'Cancel' | translate }}\" color=\"neutral\" (click)=\"editCancelled.emit()\" [buttonGroup]=\"true\" [ghost]=\"true\">\n              </e11-button>\n              <e11-button\n                value=\"{{ 'Delete' | translate }}\"\n                *ngIf=\"_notificationId\"\n                color=\"secondary\"\n                (click)=\"confirmDelete.open()\"\n                [buttonGroup]=\"true\"\n              >\n              </e11-button>\n              <e11-button value=\"{{ 'Save' | translate }}\" color=\"primary-accent\" [type]=\"'submit'\" (click)=\"save()\"> </e11-button>\n            </div>\n          </div>\n        </e11-panel>\n      </ng-container>\n    </ng-container>\n  </div>\n</div>\n\n<e11-confirmation\n  #confirmDelete\n  confirmTitle=\"{{ 'Delete notification' | translate }}\"\n  confirmText=\"{{ 'Delete notification' | translate }}\"\n  cancelText=\"{{ 'Cancel' | translate }}\"\n  [backdropStyle]=\"'dark'\"\n  [confirmClassOverrides]=\"'e11-bg-skin-error'\"\n  [isMobile]=\"false\"\n  (answer)=\"deleteNotification($event)\"\n>\n  {{ (\"Are you sure you want to delete this notification with ID: \" | translate) + _notificationId }}\n</e11-confirmation>\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i7.E11PanelComponent, selector: "e11-panel", inputs: ["size", "headerText", "headerSize", "headerBgColor", "headerFontColor", "headerStyle", "ctaIcon", "ctaIconType", "ctaIconSize", "ctaText", "ctaFontColor", "hasBorder", "hasShadow", "containerClassOverrides", "contentClassOverrides", "headerTemplate", "bgOpacity"], outputs: ["ctaClicked"] }, { kind: "directive", type: i8$1.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i9$1.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "component", type: i10.E11SelectComponent, selector: "e11-select", inputs: ["id", "name", "label", "size", "selectMessage", "labelClass", "inputClass", "containerClass", "parentForm", "formControlName", "options", "value", "hasError", "required", "disabled", "loading", "tooltipConfig", "labelTextOptional", "selectClass"], outputs: ["valueChange", "onValueChange"] }, { kind: "directive", type: i11.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: i12.E11ConfirmationComponent, selector: "e11-confirmation", inputs: ["confirmTitle", "confirmText", "cancelText", "confirmClassOverrides", "cancelClassOverrides", "isMobile", "processing", "processingText", "closeOnEscape", "closeOnClickOutside", "hasBackdrop", "backdropStyle", "confirmValueWithInputBox", "valueToConfirm"], outputs: ["answer"] }, { kind: "component", type: NotificationTestComponent, selector: "notification-test", inputs: ["notificationId"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditNotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-edit-notification', template: "<h2 class=\"e11-text-skin-primary-accent e11-mb-4\">{{ \"Edit Notification\" | translate }}</h2>\n<div class=\"e11-flex e11-flex-col lg:e11-flex-row e11-gap-6\">\n  <div class=\"e11-w-full lg:e11-w-1/2\">\n    <ng-container *ngrxLet=\"emailTemplatesLoaded$ as emailTemplatesLoaded\">\n      <ng-container *ngrxLet=\"htmlPreview$ as htmlPreview\">\n        <ng-container *ngrxLet=\"emailTemplates$ as emailTemplates\">\n          <div>\n            <e11-select\n              [loading]=\"!emailTemplatesLoaded\"\n              [options]=\"emailTemplates ?? []\"\n              (valueChange)=\"onSelectValueChange($event)\"\n              label=\"Select an Email Template\"\n            ></e11-select>\n          </div>\n          <div *ngIf=\"htmlPreview\">\n            <e11-panel>\n              <h3 class=\"e11-mb-4\">{{ \"Email Preview\" | translate }}</h3>\n\n              <div class=\"e11-w-full\">\n                <div class=\"e11-pb-4\" [innerHTML]=\"htmlPreview\"></div>\n              </div>\n            </e11-panel>\n          </div>\n        </ng-container>\n      </ng-container>\n    </ng-container>\n  </div>\n  <div class=\"e11-w-full lg:e11-w-1/2\">\n    <ng-container *ngrxLet=\"notificationsLoaded$ as notificationsLoaded\">\n      <ng-container *ngrxLet=\"selectedNotification$ as selectedNotification\">\n        <e11-panel>\n          <div class=\"e11-w-full e11-flex\">\n            <div class=\"e11-grow\">\n              <h3 class=\"e11-mb-4\">{{ \"Configuration\" | translate }}</h3>\n            </div>\n            <div class=\"e11-flex e11-justify-end\">\n              <e11-button\n                [size]=\"'sm'\"\n                [color]=\"'primary-accent'\"\n                [value]=\"'Send a test notification' | translate\"\n                [skeletonLoading]=\"!notificationsLoaded\"\n                (click)=\"testNotification()\"\n              ></e11-button>\n            </div>\n          </div>\n\n          <notification-test #notificationTest [notificationId]=\"_notificationId\"></notification-test>\n\n          <div class=\"e11-w-full\">\n            <e11-dynamic-form-builder\n              #generalForm\n              [initialValue]=\"selectedNotification\"\n              [fieldGroups]=\"notificationForms.general\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <!--  nudges  -->\n            <ng-container *ngFor=\"let nudgeFieldGroups of nudgesFieldGroups; let i = index\">\n              <e11-dynamic-form-builder #notificationNudgesFormBuilder [initialValue]=\"{}\" [hideSubmitMenu]=\"true\" [fieldGroups]=\"nudgeFieldGroups\">\n                <ng-template e11Template=\"removeNudge\">\n                  <e11-button\n                    [value]=\"''\"\n                    [color]=\"'red'\"\n                    [size]=\"'sm'\"\n                    [icon]=\"'delete_forever'\"\n                    [iconClassOverrides]=\"'-e11-mr-1'\"\n                    (click)=\"removeNudge(i)\"\n                  ></e11-button>\n                </ng-template>\n              </e11-dynamic-form-builder>\n            </ng-container>\n            <e11-button\n              [disabled]=\"!notificationsLoaded\"\n              [value]=\"'Add Nudge' | translate\"\n              [color]=\"'primary-accent'\"\n              [size]=\"'sm'\"\n              (click)=\"addNewNudge()\"\n            ></e11-button>\n            <div class=\"e11-mb-8\"></div>\n\n            <h3 class=\"e11-mb-4\">{{ \"Email\" | translate }}</h3>\n            <e11-dynamic-form-builder\n              #emailForm\n              [initialValue]=\"selectedNotification?.email\"\n              [fieldGroups]=\"notificationForms.email\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <h3 class=\"e11-mb-4\">{{ \"Push\" }}</h3>\n            <e11-dynamic-form-builder\n              #pushForm\n              [initialValue]=\"selectedNotification?.push\"\n              [fieldGroups]=\"notificationForms.push\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n            <h3 class=\"e11-mb-4\">{{ \"In-App\" }}</h3>\n            <e11-dynamic-form-builder\n              #inAppForm\n              [initialValue]=\"selectedNotification?.inApp\"\n              [fieldGroups]=\"notificationForms.inApp\"\n              [loading]=\"!notificationsLoaded\"\n              [hideSubmitMenu]=\"true\"\n            ></e11-dynamic-form-builder>\n          </div>\n          <div class=\"e11-flex\">\n            <div class=\"e11-ml-auto\">\n              <e11-button value=\"{{ 'Cancel' | translate }}\" color=\"neutral\" (click)=\"editCancelled.emit()\" [buttonGroup]=\"true\" [ghost]=\"true\">\n              </e11-button>\n              <e11-button\n                value=\"{{ 'Delete' | translate }}\"\n                *ngIf=\"_notificationId\"\n                color=\"secondary\"\n                (click)=\"confirmDelete.open()\"\n                [buttonGroup]=\"true\"\n              >\n              </e11-button>\n              <e11-button value=\"{{ 'Save' | translate }}\" color=\"primary-accent\" [type]=\"'submit'\" (click)=\"save()\"> </e11-button>\n            </div>\n          </div>\n        </e11-panel>\n      </ng-container>\n    </ng-container>\n  </div>\n</div>\n\n<e11-confirmation\n  #confirmDelete\n  confirmTitle=\"{{ 'Delete notification' | translate }}\"\n  confirmText=\"{{ 'Delete notification' | translate }}\"\n  cancelText=\"{{ 'Cancel' | translate }}\"\n  [backdropStyle]=\"'dark'\"\n  [confirmClassOverrides]=\"'e11-bg-skin-error'\"\n  [isMobile]=\"false\"\n  (answer)=\"deleteNotification($event)\"\n>\n  {{ (\"Are you sure you want to delete this notification with ID: \" | translate) + _notificationId }}\n</e11-confirmation>\n" }]
        }], ctorParameters: () => [{ type: i4$1.E11Logger }, { type: NotificationConfigStore }, { type: HtmlPreviewService }, { type: EditNotificationForm }], propDecorators: { notificationNudgesFormBuilders: [{
                type: ViewChildren,
                args: ['notificationNudgesFormBuilder']
            }], generalForm: [{
                type: ViewChild,
                args: ['generalForm']
            }], emailForm: [{
                type: ViewChild,
                args: ['emailForm']
            }], pushForm: [{
                type: ViewChild,
                args: ['pushForm']
            }], inAppForm: [{
                type: ViewChild,
                args: ['inAppForm']
            }], notificationId: [{
                type: Input
            }], notificationTest: [{
                type: ViewChild,
                args: ['notificationTest']
            }], confirmDelete: [{
                type: ViewChild,
                args: ['confirmDelete']
            }], editCancelled: [{
                type: Output
            }], notificationDeleted: [{
                type: Output
            }], notificationSaved: [{
                type: Output
            }] } });

class UiNotificationConfigComponent {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationConfigComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiNotificationConfigComponent, selector: "ui-notification-config", inputs: { notification: "notification" }, ngImport: i0, template: `
    <div class="e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2 e11-cursor-pointer hover:e11-bg-skin-primary-accent/10">
      <div class="e11-truncate e11-pr-2">
        <div class="e11-w-full e11-skeleton-empty">
          {{ notification?.group }}
        </div>

        <div class="e11-w-full e11-text-sm e11-text-skin-light e11-truncate e11-skeleton-empty">{{ notification?.id }}</div>
      </div>
      <div class="e11-truncate e11-px-2 e11-skeleton-empty">{{ notification?.name }}</div>
      <div class="e11-truncate e11-px-2 e11-skeleton-empty">{{ notification?.sentWhen }}</div>
    </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationConfigComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ui-notification-config',
                    template: `
    <div class="e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2 e11-cursor-pointer hover:e11-bg-skin-primary-accent/10">
      <div class="e11-truncate e11-pr-2">
        <div class="e11-w-full e11-skeleton-empty">
          {{ notification?.group }}
        </div>

        <div class="e11-w-full e11-text-sm e11-text-skin-light e11-truncate e11-skeleton-empty">{{ notification?.id }}</div>
      </div>
      <div class="e11-truncate e11-px-2 e11-skeleton-empty">{{ notification?.name }}</div>
      <div class="e11-truncate e11-px-2 e11-skeleton-empty">{{ notification?.sentWhen }}</div>
    </div>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { notification: [{
                type: Input
            }] } });

class ListNotificationsComponent {
    constructor(formBuilder, notificationConfigStore, logger) {
        this.formBuilder = formBuilder;
        this.notificationConfigStore = notificationConfigStore;
        this.logger = logger;
        this.itemSelected = new EventEmitter();
        this.newButtonClicked = new EventEmitter();
        this.notifications$ = this.notificationConfigStore.notifications$;
        this.notificationsLoaded$ = this.notificationConfigStore.notificationsLoaded$;
        this.loading = false;
        this.destroy$ = new Subject();
        this.searchEngine = new SearchEngine({
            fields: ['name', 'sentWhen', 'group', 'id'],
            storeFields: ['name', 'sentWhen', 'group', 'id'],
            searchOptions: {
                fuzzy: 0.4,
            },
        });
        this.formSearch = new UntypedFormGroup({});
    }
    async ngOnInit() {
        this.loading = true;
        this.formSearch = this.formBuilder.group({ search: new UntypedFormControl('') });
        await this.fetchAndIndexNotifications();
        this.loading = false;
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    search() {
        const searchValue = this.formSearch.value.search;
        // '' is falsy
        if (searchValue)
            this.notifications = this.searchEngine.search(searchValue);
        else
            this.notifications = this.allNotifications;
    }
    async fetchAndIndexNotifications() {
        this.notificationConfigStore.getNotificationsIfNotLoaded();
        const [notifications] = await firstValueFrom(combineLatest([this.notifications$, this.notificationsLoaded$.pipe(filter(isTrue))]));
        this.logger.log('ListNotificationsComponent', notifications);
        this.notifications = notifications;
        this.allNotifications = new Array().concat(notifications);
        this.searchEngine.addAll(notifications);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListNotificationsComponent, deps: [{ token: i1$2.UntypedFormBuilder }, { token: NotificationConfigStore }, { token: i4$1.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: ListNotificationsComponent, selector: "sdk-list-notifications", outputs: { itemSelected: "itemSelected", newButtonClicked: "newButtonClicked" }, ngImport: i0, template: `
    <div class="content-main">
      <div class="e11-w-full e11-flex">
        <div class="e11-grow">
          <h2 class="e11-text-skin-primary-accent">{{ 'Manage Notifications' | translate }}</h2>
        </div>
        <div class="e11-flex e11-justify-end">
          <e11-button (click)="newButtonClicked.emit()" color="primary-accent" value="{{ 'New Notification' | translate }}"></e11-button>
        </div>
      </div>

      <div class="e11-relative">
        <div class="e11-w-full e11-mb-16">
          <div class="e11-grid e11-gap-4 e11-items-center e11-grid-cols-1 md:e11-grid-cols-3 e11-mb-0 e11-pb-3">
            <div class="">
              <form class="e11-w-full" [formGroup]="formSearch" (ngSubmit)="search()" novalidate>
                <e11-input
                  [placeholder]="'Search for items' | translate"
                  [parentForm]="formSearch"
                  formControlName="search"
                  [size]="'md'"
                  [autofocus]="true"
                  labelColor="secondary"
                  [leadingIcon]="'search'"
                ></e11-input>
              </form>
            </div>
            <div class=""></div>
            <div class=""></div>
          </div>

          <e11-panel>
            <div class="e11-grid e11-grid-cols-3 e11-w-full e11-pb-4 e11-text-sm md:e11-text-base e11-border-b e11-border-skin-app-borders">
              <div class="e11-font-bold e11-pl-2">{{ 'Group' | translate }}</div>
              <div class="e11-font-bold e11-pl-2">{{ 'Name' | translate }}</div>
              <div class="e11-font-bold e11-pl-2">{{ 'Sent When' | translate }}</div>
            </div>

            <!-- Skeleton Loaders -->
            <div *ngIf="loading">
              <div *ngFor="let i of 2 | range" class="e11-border-b e11-border-skin-app-borders odd:e11-bg-skin-grey/10 last:e11-border-transparent">
                <ui-notification-config [notification]="undefined"></ui-notification-config>
              </div>
            </div>

            <div
              *ngFor="let notification of notifications"
              class="e11-border-b e11-border-skin-app-borders odd:e11-bg-skin-grey/10 last:e11-border-transparent"
            >
              <ui-notification-config [notification]="notification" (click)="itemSelected.emit(notification.id)"></ui-notification-config>
            </div>
          </e11-panel>
        </div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i7.E11PanelComponent, selector: "e11-panel", inputs: ["size", "headerText", "headerSize", "headerBgColor", "headerFontColor", "headerStyle", "ctaIcon", "ctaIconType", "ctaIconSize", "ctaText", "ctaFontColor", "hasBorder", "hasShadow", "containerClassOverrides", "contentClassOverrides", "headerTemplate", "bgOpacity"], outputs: ["ctaClicked"] }, { kind: "component", type: i7$1.E11InputComponent, selector: "e11-input", inputs: ["containerClass", "inputClass", "hintText", "label", "type", "size", "style", "placeholder", "maxLength", "max", "name", "id", "iconLeft", "iconLeftBorder", "required", "hasError", "labelColor", "borderGlow", "checkIcon", "clearIcon", "autofocus", "togglePassword", "hasButton", "buttonText", "buttonIcon", "buttonIconType", "buttonIconSize", "readonly", "disabled", "disabledText", "autocomplete", "formControlName", "isRounded", "parentForm", "tooltipConfig", "icon", "iconType", "iconSize", "darkMode", "context", "enforceCharacterLimit", "showCharacterLimitWarning", "loading", "leadingIcon", "trailingIcon"], outputs: ["change", "focus", "blur"] }, { kind: "component", type: UiNotificationConfigComponent, selector: "ui-notification-config", inputs: ["notification"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }, { kind: "pipe", type: i2$2.RangePipe, name: "range" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListNotificationsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-list-notifications',
                    template: `
    <div class="content-main">
      <div class="e11-w-full e11-flex">
        <div class="e11-grow">
          <h2 class="e11-text-skin-primary-accent">{{ 'Manage Notifications' | translate }}</h2>
        </div>
        <div class="e11-flex e11-justify-end">
          <e11-button (click)="newButtonClicked.emit()" color="primary-accent" value="{{ 'New Notification' | translate }}"></e11-button>
        </div>
      </div>

      <div class="e11-relative">
        <div class="e11-w-full e11-mb-16">
          <div class="e11-grid e11-gap-4 e11-items-center e11-grid-cols-1 md:e11-grid-cols-3 e11-mb-0 e11-pb-3">
            <div class="">
              <form class="e11-w-full" [formGroup]="formSearch" (ngSubmit)="search()" novalidate>
                <e11-input
                  [placeholder]="'Search for items' | translate"
                  [parentForm]="formSearch"
                  formControlName="search"
                  [size]="'md'"
                  [autofocus]="true"
                  labelColor="secondary"
                  [leadingIcon]="'search'"
                ></e11-input>
              </form>
            </div>
            <div class=""></div>
            <div class=""></div>
          </div>

          <e11-panel>
            <div class="e11-grid e11-grid-cols-3 e11-w-full e11-pb-4 e11-text-sm md:e11-text-base e11-border-b e11-border-skin-app-borders">
              <div class="e11-font-bold e11-pl-2">{{ 'Group' | translate }}</div>
              <div class="e11-font-bold e11-pl-2">{{ 'Name' | translate }}</div>
              <div class="e11-font-bold e11-pl-2">{{ 'Sent When' | translate }}</div>
            </div>

            <!-- Skeleton Loaders -->
            <div *ngIf="loading">
              <div *ngFor="let i of 2 | range" class="e11-border-b e11-border-skin-app-borders odd:e11-bg-skin-grey/10 last:e11-border-transparent">
                <ui-notification-config [notification]="undefined"></ui-notification-config>
              </div>
            </div>

            <div
              *ngFor="let notification of notifications"
              class="e11-border-b e11-border-skin-app-borders odd:e11-bg-skin-grey/10 last:e11-border-transparent"
            >
              <ui-notification-config [notification]="notification" (click)="itemSelected.emit(notification.id)"></ui-notification-config>
            </div>
          </e11-panel>
        </div>
      </div>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: i1$2.UntypedFormBuilder }, { type: NotificationConfigStore }, { type: i4$1.E11Logger }], propDecorators: { itemSelected: [{
                type: Output
            }], newButtonClicked: [{
                type: Output
            }] } });

class ListTemplatesComponent {
    constructor(emailTemplateRepository) {
        this.emailTemplateRepository = emailTemplateRepository;
        this.loading = false;
        this.destroy$ = new Subject();
        this.itemSelected = new EventEmitter();
        this.newButtonClicked = new EventEmitter();
    }
    ngOnInit() {
        this.loading = true;
        this.emailTemplateRepository
            .getAllValueChanges()
            .pipe(takeUntil(this.destroy$))
            .subscribe((templates) => {
            this.emailTemplates = templates;
            this.loading = false;
        });
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    onItemSelected(id) {
        this.itemSelected.emit(id);
    }
    onNewButtonClicked() {
        this.newButtonClicked.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListTemplatesComponent, deps: [{ token: EmailTemplateRepository }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: ListTemplatesComponent, selector: "sdk-list-email-templates", outputs: { itemSelected: "itemSelected", newButtonClicked: "newButtonClicked" }, ngImport: i0, template: "<div class=\"content-main\">\n  <div class=\"e11-w-full e11-flex e11-mb-4\">\n    <div class=\"e11-grow\">\n      <h2 class=\"e11-text-skin-primary-accent\">{{ \"Email Templates\" | translate }}</h2>\n    </div>\n    <div class=\"e11-flex e11-justify-end\">\n      <e11-button (click)=\"onNewButtonClicked()\" color=\"primary-accent\" value=\"New Email Template\"></e11-button>\n    </div>\n  </div>\n\n  <div class=\"e11-relative\">\n    <!-- Loader -->\n    <!-- <div *ngIf=\"loading\" class=\"e11-loader-fading e11-w-full e11-flex e11-text-center e11-items-center e11-justify-center e11-absolute e11-top-16\">\n      <e11-loader-dots\n        [size]=\"'md'\"\n        [color]=\"'e11-bg-skin-primary-accent'\"\n        label=\"{{ 'Retrieving notification designs...' | translate }}\"\n      ></e11-loader-dots>\n    </div> -->\n    <!-- Data -->\n    <ng-container>\n      <div class=\"e11-w-full e11-mb-16\">\n        <!-- Filter / Search -->\n        <div class=\"e11-grid e11-gap-4 e11-items-center e11-grid-cols-1 md:e11-grid-cols-3 e11-mb-0\">\n          <div class=\"\">\n            <!-- TODO: Add Search Box Here -->\n          </div>\n          <div class=\"\"></div>\n          <div class=\"\"></div>\n        </div>\n\n        <e11-panel>\n          <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-px-2 e11-pb-4 e11-text-sm md:e11-text-base e11-border-b e11-border-skin-app-borders\">\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Name\" | translate }}\n            </div>\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Description\" | translate }}\n            </div>\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Id\" | translate }}\n            </div>\n          </div>\n          <!-- Skeleton Loaders -->\n          <div *ngIf=\"loading\">\n            <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2 e11-border-b e11-border-skin-app-borders\">\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n            </div>\n            <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2\">\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n            </div>\n          </div>\n          <ng-container *ngIf=\"!loading\">\n            <div\n              class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-px-2 e11-py-4 e11-border-b e11-border-skin-app-borders e11-transition e11-duration-200 e11-ease-in-out e11-cursor-pointer hover:e11-bg-skin-primary-accent/10 odd:e11-bg-skin-grey/10 last:e11-border-transparent\"\n              *ngFor=\"let template of emailTemplates\"\n              (click)=\"onItemSelected(template.id)\"\n            >\n              <div class=\"e11-truncate\">{{ template.name }}</div>\n              <div class=\"e11-truncate\">{{ template.description }}</div>\n              <div class=\"e11-truncate\">{{ template.id }}</div>\n            </div>\n          </ng-container>\n        </e11-panel>\n      </div>\n    </ng-container>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i7.E11PanelComponent, selector: "e11-panel", inputs: ["size", "headerText", "headerSize", "headerBgColor", "headerFontColor", "headerStyle", "ctaIcon", "ctaIconType", "ctaIconSize", "ctaText", "ctaFontColor", "hasBorder", "hasShadow", "containerClassOverrides", "contentClassOverrides", "headerTemplate", "bgOpacity"], outputs: ["ctaClicked"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ListTemplatesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-list-email-templates', template: "<div class=\"content-main\">\n  <div class=\"e11-w-full e11-flex e11-mb-4\">\n    <div class=\"e11-grow\">\n      <h2 class=\"e11-text-skin-primary-accent\">{{ \"Email Templates\" | translate }}</h2>\n    </div>\n    <div class=\"e11-flex e11-justify-end\">\n      <e11-button (click)=\"onNewButtonClicked()\" color=\"primary-accent\" value=\"New Email Template\"></e11-button>\n    </div>\n  </div>\n\n  <div class=\"e11-relative\">\n    <!-- Loader -->\n    <!-- <div *ngIf=\"loading\" class=\"e11-loader-fading e11-w-full e11-flex e11-text-center e11-items-center e11-justify-center e11-absolute e11-top-16\">\n      <e11-loader-dots\n        [size]=\"'md'\"\n        [color]=\"'e11-bg-skin-primary-accent'\"\n        label=\"{{ 'Retrieving notification designs...' | translate }}\"\n      ></e11-loader-dots>\n    </div> -->\n    <!-- Data -->\n    <ng-container>\n      <div class=\"e11-w-full e11-mb-16\">\n        <!-- Filter / Search -->\n        <div class=\"e11-grid e11-gap-4 e11-items-center e11-grid-cols-1 md:e11-grid-cols-3 e11-mb-0\">\n          <div class=\"\">\n            <!-- TODO: Add Search Box Here -->\n          </div>\n          <div class=\"\"></div>\n          <div class=\"\"></div>\n        </div>\n\n        <e11-panel>\n          <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-px-2 e11-pb-4 e11-text-sm md:e11-text-base e11-border-b e11-border-skin-app-borders\">\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Name\" | translate }}\n            </div>\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Description\" | translate }}\n            </div>\n            <div class=\"e11-font-primary-demibold e11-w-fit\">\n              {{ \"Id\" | translate }}\n            </div>\n          </div>\n          <!-- Skeleton Loaders -->\n          <div *ngIf=\"loading\">\n            <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2 e11-border-b e11-border-skin-app-borders\">\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n            </div>\n            <div class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-p-2\">\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n              <div class=\"e11-skeleton-empty\"></div>\n            </div>\n          </div>\n          <ng-container *ngIf=\"!loading\">\n            <div\n              class=\"e11-grid e11-grid-cols-3 e11-w-full e11-text-sm md:e11-text-base e11-px-2 e11-py-4 e11-border-b e11-border-skin-app-borders e11-transition e11-duration-200 e11-ease-in-out e11-cursor-pointer hover:e11-bg-skin-primary-accent/10 odd:e11-bg-skin-grey/10 last:e11-border-transparent\"\n              *ngFor=\"let template of emailTemplates\"\n              (click)=\"onItemSelected(template.id)\"\n            >\n              <div class=\"e11-truncate\">{{ template.name }}</div>\n              <div class=\"e11-truncate\">{{ template.description }}</div>\n              <div class=\"e11-truncate\">{{ template.id }}</div>\n            </div>\n          </ng-container>\n        </e11-panel>\n      </div>\n    </ng-container>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: EmailTemplateRepository }], propDecorators: { itemSelected: [{
                type: Output
            }], newButtonClicked: [{
                type: Output
            }] } });

var HtmlEditorDisplayMode;
(function (HtmlEditorDisplayMode) {
    HtmlEditorDisplayMode["EMAIL"] = "email";
    HtmlEditorDisplayMode["WEB"] = "web";
})(HtmlEditorDisplayMode || (HtmlEditorDisplayMode = {}));
var TemplateCategory;
(function (TemplateCategory) {
    TemplateCategory["Transactional"] = "Transactional";
})(TemplateCategory || (TemplateCategory = {}));

const options = [{ name: TemplateCategory.Transactional, value: TemplateCategory.Transactional }];
class EditTemplateComponent {
    constructor(emailTemplateRepository, e11NotificationService, router) {
        this.emailTemplateRepository = emailTemplateRepository;
        this.e11NotificationService = e11NotificationService;
        this.router = router;
        this.destroy$ = new Subject();
        this.editorDesignLoaded$ = new Subject();
        this.displayMode = HtmlEditorDisplayMode.EMAIL;
        this.fieldGroups = e11FormFor()([
            e11FormLayoutInlineGrid([
                e11FieldInputText('name', 'Name', undefined, { validatorOrOpts: [Validators.required] }),
                e11FieldInputSelect('category', 'Category', options, undefined, { validatorOrOpts: [Validators.required] }),
                e11FieldInputText('description', 'Description', undefined, { validatorOrOpts: [Validators.required] }),
            ]),
        ]);
    }
    ngOnInit() { }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    async save(formData) {
        const editorOutput = await this.emailEditor.save();
        if (this.templateId) {
            await this.emailTemplateRepository.update({ ...formData, editorOutput: editorOutput, id: this.templateId });
        }
        else {
            await this.emailTemplateRepository.add({ ...formData, editorOutput: editorOutput });
        }
        const message = 'Template Saved';
        this.e11NotificationService.popNotificationMessage({ title: 'Saved', type: 'success', message });
    }
    async delete(thing) {
        console.log('::::: delete', thing);
    }
    async cancel() {
        this.router.navigate(['platform-admin/manage-notification-templates']);
    }
    onHtmlEditorLoaded(loaded) {
        if (loaded && this.templateId) {
            this.emailTemplateRepository
                .get(this.templateId)
                .pipe(takeUntil(this.editorDesignLoaded$), filter(isNotNil))
                .subscribe((design) => {
                const { name, description, category } = design;
                this.emailDesignFormValue = {
                    name,
                    description,
                    category,
                };
                this.emailEditor.loadDesign(design.editorOutput.design);
                //this.emailEditor.loadDesign({ html: design.editorOutput.html, classic: true })
                this.editorDesignLoaded$.next(true);
                this.editorDesignLoaded$.complete();
            });
        }
    }
    onHtmlEditorError(error) {
        const message = error.message;
        this.e11NotificationService.popNotificationMessage({ title: 'Error', type: 'error', message });
    }
    onHtmlEditorSaved(emailTemplate) { }
    onHtmlEditorLoading(loading) { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditTemplateComponent, deps: [{ token: EmailTemplateRepository }, { token: i2$1.E11NotificationsService }, { token: i3$3.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: EditTemplateComponent, selector: "sdk-edit-email-templates", inputs: { templateId: "templateId" }, viewQueries: [{ propertyName: "emailEditor", first: true, predicate: ["emailEditor"], descendants: true }], ngImport: i0, template: "<div class=\"content-main\">\n  <div class=\"e11-w-full e11-flex e11-mb-4\">\n    <div class=\"e11-grow\">\n      <h2 class=\"e11-text-skin-primary-accent\">{{ \"Email Template\" | translate }}</h2>\n    </div>\n    <div class=\"e11-flex e11-justify-end\"></div>\n  </div>\n\n  <div class=\"e11-w-full e11-pb-1\">\n    <e11-dynamic-form-builder\n      #dynamicForm\n      [initialValue]=\"emailDesignFormValue\"\n      [fieldGroups]=\"fieldGroups\"\n      [showCancelLink]=\"true\"\n      [showDeleteButton]=\"true\"\n      (onSubmit)=\"save($event)\"\n      (onCancel)=\"cancel()\"\n      (onDelete)=\"delete($event)\"\n    ></e11-dynamic-form-builder>\n  </div>\n\n  <div class=\"e11-w-full\">\n    <sdk-notifications-html-editor\n      #emailEditor\n      [hideSaveButton]=\"true\"\n      [displayMode]=\"displayMode\"\n      (editorLoaded)=\"onHtmlEditorLoaded($event)\"\n      (error)=\"onHtmlEditorError($event)\"\n      (saved)=\"onHtmlEditorSaved($event)\"\n      (loading)=\"onHtmlEditorLoading($event)\"\n    ></sdk-notifications-html-editor>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: i9$1.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "component", type: HtmlEditorComponent, selector: "sdk-notifications-html-editor", inputs: ["minHeight", "displayMode", "hideSaveButton"], outputs: ["error", "saved", "editorLoaded", "loading"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: EditTemplateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-edit-email-templates', template: "<div class=\"content-main\">\n  <div class=\"e11-w-full e11-flex e11-mb-4\">\n    <div class=\"e11-grow\">\n      <h2 class=\"e11-text-skin-primary-accent\">{{ \"Email Template\" | translate }}</h2>\n    </div>\n    <div class=\"e11-flex e11-justify-end\"></div>\n  </div>\n\n  <div class=\"e11-w-full e11-pb-1\">\n    <e11-dynamic-form-builder\n      #dynamicForm\n      [initialValue]=\"emailDesignFormValue\"\n      [fieldGroups]=\"fieldGroups\"\n      [showCancelLink]=\"true\"\n      [showDeleteButton]=\"true\"\n      (onSubmit)=\"save($event)\"\n      (onCancel)=\"cancel()\"\n      (onDelete)=\"delete($event)\"\n    ></e11-dynamic-form-builder>\n  </div>\n\n  <div class=\"e11-w-full\">\n    <sdk-notifications-html-editor\n      #emailEditor\n      [hideSaveButton]=\"true\"\n      [displayMode]=\"displayMode\"\n      (editorLoaded)=\"onHtmlEditorLoaded($event)\"\n      (error)=\"onHtmlEditorError($event)\"\n      (saved)=\"onHtmlEditorSaved($event)\"\n      (loading)=\"onHtmlEditorLoading($event)\"\n    ></sdk-notifications-html-editor>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: EmailTemplateRepository }, { type: i2$1.E11NotificationsService }, { type: i3$3.Router }], propDecorators: { templateId: [{
                type: Input
            }], emailEditor: [{
                type: ViewChild,
                args: ['emailEditor']
            }] } });

class UiNotificationCardComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiNotificationCardComponent, selector: "ui-notification-card", inputs: { notification: "notification" }, ngImport: i0, template: ` <li class="e11-py-4 e11-px-4 e11-rounded-t-md e11-cursor-pointer hover:e11-bg-gray-100 e11-relative e11-border-b e11-border-gray-200">
    <span
      *ngIf="!notification.viewed"
      class="e11-absolute e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center e11-h-full e11-top-0 e11-left-2"
    >
      <span class="e11-h-1.5 e11-w-1.5 e11-rounded-full e11-bg-skin-primary-accent" aria-hidden="true"></span>
    </span>
    <div class="e11-flex e11-space-x-3 e11-items-center">
      <span class="material-icons-outlined" *ngIf="notification.viewed"> notifications </span>
      <span class="material-icons-outlined" *ngIf="!notification.viewed"> notifications_active </span>
      <div class="e11-flex-1 e11-space-y-1">
        <div class="e11-flex e11-items-center e11-justify-between">
          <h6 class="e11-font-medium e11-m-0 e11-pr-2" [ngClass]="{ 'e11-font-semibold e11-text-skin-primary-accent': !notification.viewed }">
            {{ notification.title }}
          </h6>
          <p class="e11-text-xs e11-text-gray-500 e11-m-0">
            <!-- {{ !notification.viewed ? 'NEW' : notification.originalNotification.__createdAt.toLocaleDateString() }} -->
            {{ notification.originalNotification.__createdAt.toLocaleDateString() }}
          </p>
        </div>
        <p class="e11-text-xs e11-text-gray-500 e11-line-clamp-2 e11-m-0">
          {{ notification.description }}
        </p>
      </div>
    </div>
  </li>`, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ui-notification-card', template: ` <li class="e11-py-4 e11-px-4 e11-rounded-t-md e11-cursor-pointer hover:e11-bg-gray-100 e11-relative e11-border-b e11-border-gray-200">
    <span
      *ngIf="!notification.viewed"
      class="e11-absolute e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center e11-h-full e11-top-0 e11-left-2"
    >
      <span class="e11-h-1.5 e11-w-1.5 e11-rounded-full e11-bg-skin-primary-accent" aria-hidden="true"></span>
    </span>
    <div class="e11-flex e11-space-x-3 e11-items-center">
      <span class="material-icons-outlined" *ngIf="notification.viewed"> notifications </span>
      <span class="material-icons-outlined" *ngIf="!notification.viewed"> notifications_active </span>
      <div class="e11-flex-1 e11-space-y-1">
        <div class="e11-flex e11-items-center e11-justify-between">
          <h6 class="e11-font-medium e11-m-0 e11-pr-2" [ngClass]="{ 'e11-font-semibold e11-text-skin-primary-accent': !notification.viewed }">
            {{ notification.title }}
          </h6>
          <p class="e11-text-xs e11-text-gray-500 e11-m-0">
            <!-- {{ !notification.viewed ? 'NEW' : notification.originalNotification.__createdAt.toLocaleDateString() }} -->
            {{ notification.originalNotification.__createdAt.toLocaleDateString() }}
          </p>
        </div>
        <p class="e11-text-xs e11-text-gray-500 e11-line-clamp-2 e11-m-0">
          {{ notification.description }}
        </p>
      </div>
    </div>
  </li>`, encapsulation: ViewEncapsulation.None }]
        }], propDecorators: { notification: [{
                type: Input
            }] } });

class UiNotificationLongCardComponent {
    constructor() {
        this.border = false;
        this.cardClicked = new EventEmitter();
        this.xClicked = new EventEmitter();
    }
    // not sure what this is for....
    xClickedCallback(event) {
        event.stopPropagation();
        this.xClicked.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationLongCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiNotificationLongCardComponent, selector: "ui-notification-long-card", inputs: { notification: "notification", border: "border" }, outputs: { cardClicked: "cardClicked", xClicked: "xClicked" }, ngImport: i0, template: `
    <li>
      <span *ngIf="!notification.viewed" class="e11-ml-2 e11-mt-7 e11-absolute e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center">
        <span class="e11-h-1.5 e11-w-1.5 e11-rounded-full e11-bg-skin-primary" aria-hidden="true"></span>
      </span>
      <a class="e11-block hover:e11-bg-skin-primary-accent/20 e11-cursor-pointer" (click)="cardClicked.emit()">
        <div class="e11-flex e11-items-center e11-px-4 e11-py-4 sm:e11-px-6">
          <div class="e11-min-w-0 e11-flex-1 e11-flex e11-items-start">
            <div class="e11-flex-shrink-0">
              <svg
                class="e11-h-8 e11-w-8 e11-rounded-full e11-self-center"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <div class="e11-min-w-0 e11-flex-1 e11-px-4 md:e11-grid md:e11-grid-cols-3 md:e11-gap-4">
              <div class="e11-col-span-2">
                <p
                  class="e11-mb-0 e11-truncate"
                  [ngClass]="{ 'e11-text-skin-primary-accent': !notification.viewed, 'e11-font-semibold e11-text-skin-primary': notification.viewed }"
                >
                  {{ notification.title }}
                </p>
                <p class="e11-flex e11-items-center e11-text-sm e11-text-skin-light e11-mb-0">
                  <span class="">
                    {{ notification.description }}
                  </span>
                </p>
              </div>
              <div class="e11-hidden md:e11-block e11-ml-auto e11-self-center">
                <div>
                  <p class="e11-text-sm e11-text-gray-500 e11-mb-0 ">
                    <time [dateTime]="notification.originalNotification.__createdAt.toLocaleDateString()">
                      {{ notification.originalNotification.__createdAt.toLocaleDateString() }}</time
                    >
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <svg
              (click)="xClickedCallback($event)"
              xmlns="http://www.w3.org/2000/svg"
              class="e11-h-5 e11-w-5 e11-text-gray-400 hover:e11-text-gray-600"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </a>
    </li>
  `, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationLongCardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ui-notification-long-card', template: `
    <li>
      <span *ngIf="!notification.viewed" class="e11-ml-2 e11-mt-7 e11-absolute e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center">
        <span class="e11-h-1.5 e11-w-1.5 e11-rounded-full e11-bg-skin-primary" aria-hidden="true"></span>
      </span>
      <a class="e11-block hover:e11-bg-skin-primary-accent/20 e11-cursor-pointer" (click)="cardClicked.emit()">
        <div class="e11-flex e11-items-center e11-px-4 e11-py-4 sm:e11-px-6">
          <div class="e11-min-w-0 e11-flex-1 e11-flex e11-items-start">
            <div class="e11-flex-shrink-0">
              <svg
                class="e11-h-8 e11-w-8 e11-rounded-full e11-self-center"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <div class="e11-min-w-0 e11-flex-1 e11-px-4 md:e11-grid md:e11-grid-cols-3 md:e11-gap-4">
              <div class="e11-col-span-2">
                <p
                  class="e11-mb-0 e11-truncate"
                  [ngClass]="{ 'e11-text-skin-primary-accent': !notification.viewed, 'e11-font-semibold e11-text-skin-primary': notification.viewed }"
                >
                  {{ notification.title }}
                </p>
                <p class="e11-flex e11-items-center e11-text-sm e11-text-skin-light e11-mb-0">
                  <span class="">
                    {{ notification.description }}
                  </span>
                </p>
              </div>
              <div class="e11-hidden md:e11-block e11-ml-auto e11-self-center">
                <div>
                  <p class="e11-text-sm e11-text-gray-500 e11-mb-0 ">
                    <time [dateTime]="notification.originalNotification.__createdAt.toLocaleDateString()">
                      {{ notification.originalNotification.__createdAt.toLocaleDateString() }}</time
                    >
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <svg
              (click)="xClickedCallback($event)"
              xmlns="http://www.w3.org/2000/svg"
              class="e11-h-5 e11-w-5 e11-text-gray-400 hover:e11-text-gray-600"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </a>
    </li>
  `, encapsulation: ViewEncapsulation.None }]
        }], propDecorators: { notification: [{
                type: Input
            }], border: [{
                type: Input
            }], cardClicked: [{
                type: Output
            }], xClicked: [{
                type: Output
            }] } });

const allForUserQuery = (userId) => new FirestoreQueryBuilder().where('userId', '==', userId).orderBy('__createdAt', OrderByDirection.Desc).build();
const unreadNotificationsQuery = (userId) => new FirestoreQueryBuilder().where('userId', '==', userId).where('viewed', '==', false).orderBy('__createdAt', OrderByDirection.Desc).build();
const notificationsForUserPageQuery = (userId, limit = DEFAULT_PAGE_SIZE) => new FirestoreQueryBuilder().where('userId', '==', userId).limit(limit).orderBy('__createdAt', OrderByDirection.Desc).build();
const unreadNotificationsPageQuery = (userId, limit = UNREAD_NOTIFICATIONS_PAGE_SIZE) => new FirestoreQueryBuilder()
    .where('userId', '==', userId)
    .where('viewed', '==', false)
    .limit(limit)
    .orderBy('__createdAt', OrderByDirection.Desc)
    .build();
class AppNotificationRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = 'notifications_app_notification/';
    }
    getPager(userId, limit = DEFAULT_PAGE_SIZE) {
        const query = (fn = (builder) => builder) => fn(new FirestoreQueryBuilder().where('userId', '==', userId).orderBy('__createdAt', OrderByDirection.Desc).limit(limit)).build();
        return new Pager((latest) => this._querySnapshot(this.buildPath(), query((builder) => builder.startAfter(latest))), (doc) => this.deserialize(doc));
    }
    getInfiniteScroller(userId, limit = DEFAULT_PAGE_SIZE) {
        const query = new FirestoreQueryBuilder().where('userId', '==', userId).orderBy('__createdAt', OrderByDirection.Desc).limit(limit).build();
        return this.getInfiniteScroll(query, '__createdAt', OrderByDirection.Desc);
    }
    unreadNotificationsLimitValueChanges(userId, limit = UNREAD_NOTIFICATIONS_PAGE_SIZE) {
        const query = unreadNotificationsPageQuery(userId, limit);
        return this.queryValueChanges(query);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    unreadNotifications(userId) {
        const query = unreadNotificationsQuery(userId);
        return this.query(query);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    unreadNotificationsValueChanges(userId) {
        const query = unreadNotificationsQuery(userId);
        return this.queryValueChanges(query);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    getAllForUser(userId) {
        const query = allForUserQuery(userId);
        return this.query(query);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    getAllForUserValueChanges(userId) {
        const query = allForUserQuery(userId);
        return this.queryValueChanges(query);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppNotificationRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppNotificationRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AppNotificationRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class UserAppNotificationService {
    constructor(repository) {
        this.repository = repository;
    }
    getPager(userId, limit = DEFAULT_PAGE_SIZE) {
        return this.repository.getPager(userId, limit);
    }
    getInfiniteScroll(userId, limit = DEFAULT_PAGE_SIZE) {
        return this.repository.getInfiniteScroller(userId, limit);
    }
    unreadNotificationsLimitValueChanges(userId, limit = UNREAD_NOTIFICATIONS_PAGE_SIZE) {
        return this.repository.unreadNotificationsLimitValueChanges(userId, limit);
    }
    /**
     * T should be a Discriminated Union of all possible notification types
     * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#discriminated-unions
     *
     * @deprecated use a paginated query
     * @param userId
     */
    unreadNotificationsValueChanges(userId) {
        return this.repository.unreadNotificationsValueChanges(userId);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    unreadNotifications(userId) {
        return this.repository.unreadNotifications(userId);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    getAll(userId) {
        return this.repository.getAllForUser(userId);
    }
    /**
     * @deprecated use a paginated query
     * @param userId
     * @returns
     */
    getAllValuesChanges(userId) {
        return this.repository.getAllForUserValueChanges(userId);
    }
    markNotificationAsRead(notificationId) {
        return this.repository.update({ id: notificationId, viewed: true });
    }
    deleteNotification(notificationId) {
        return this.repository.delete(notificationId);
    }
    async markAllAsRead(userId) {
        const notifications = await this.unreadNotifications(userId).toPromise();
        const promises = [];
        notifications.forEach((notification) => {
            promises.push(this.repository.update({ id: notification.id, viewed: true }));
        });
        await Promise.all(promises);
    }
    async deleteAllNotifications(userId) {
        const notifications = await this.getAll(userId).toPromise();
        await Promise.all(notifications.map((notification) => this.repository.delete(notification.id)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserAppNotificationService, deps: [{ token: AppNotificationRepository }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserAppNotificationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UserAppNotificationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AppNotificationRepository }] });

class NotificationHandlerFactory {
    constructor(handlers) {
        this.handlerMap = new Map();
        handlers.forEach((handler) => this.handlerMap.set(handler.notificationType, handler));
    }
    getHandlerForNotification(notification) {
        const handler = this.handlerMap.get(notification.type);
        if (!handler) {
            throw e11Error({
                title: 'Unknown notification',
                message: `No notification handler found for type ${notification.type}`,
                type: 'notification/no-handler',
                additionalData: { notification },
            });
        }
        return handler;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerFactory, deps: [{ token: APP_NOTIFICATION_HANDLER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [APP_NOTIFICATION_HANDLER_TOKEN]
                }] }] });

function toViewModel(notification) {
    return { logoUrl: '', title: notification.subject, description: notification.text, viewed: notification.viewed, originalNotification: notification };
}
class NotificationHandlerService {
    constructor(factory) {
        this.factory = factory;
    }
    toViewModel(notification) {
        return toViewModel(notification);
    }
    onClick(notification) {
        return this.factory.getHandlerForNotification(notification).onClick(notification);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerService, deps: [{ token: NotificationHandlerFactory }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: NotificationHandlerFactory }] });

const initialState = {
    notifications: [],
    unreadNotifications: [],
    fetchedAll: false,
    loaded: false,
    errors: [],
    viewed: new Map(),
    deleted: new Set(),
};
/**
 * Store to manage in-app notifications
 */
class NotificationStore extends ComponentStore {
    constructor(libConfig, userAppNotificationService, notificationHandler, errorHandler) {
        super(initialState);
        this.libConfig = libConfig;
        this.userAppNotificationService = userAppNotificationService;
        this.notificationHandler = notificationHandler;
        this.errorHandler = errorHandler;
        this.userId$ = this.libConfig.config.userId.pipe(filter$1(isNotNil));
        // SELECTORS
        this.getState = this.select((s) => s);
        this.getNotifications = this.select((s) => s.notifications.filter((n) => !s.deleted.has(n.id)).map((n) => ({ ...n, viewed: s.viewed.get(n.id) ?? n.viewed })));
        this.getNotificationVMs = this.select((s) => [s.notifications.filter((n) => !s.deleted.has(n.id)), s.viewed]).pipe(map$1(([notifications, viewed]) => this.errorHandler.errorSafeMap(notifications, (notification) => this.notificationHandler.toViewModel({ ...notification, viewed: viewed.get(notification.id) ?? notification.viewed }), notificationToVMErrorFn)));
        this.getUnreadNotificationCount = this.select((s) => s.unreadNotifications.length);
        this.getLatestNotificationVMs = (latestCount) => this.getNotificationVMs.pipe(map$1((notifications) => notifications.slice(0, latestCount)), distinctUntilChanged$1(isDeepEqual));
        this.hasFetchedAll = this.select((s) => s.fetchedAll);
        this.isLoaded = this.select((s) => s.loaded);
        // EFFECTS
        this.onGetNotificationsPage = this.effect((_$) => _$.pipe(switchMap$1((_) => {
            this.setLoading();
            this.infiniteScroll.more();
            return of(true);
        })));
        this.onListenNotifications = this.effect((_$) => _$.pipe(switchMap$1((_) => {
            return this.infiniteScroll.items.pipe(tapResponse((notifications) => this.onNotificationsChanged(notifications), (error) => this.errorHandler.handleError(error)));
        })));
        this.onListenUnreadNotificationsLimit = this.effect((userId$) => userId$.pipe(switchMap$1((userId) => {
            this.setLoading();
            return this.userAppNotificationService.unreadNotificationsLimitValueChanges(userId).pipe(tapResponse((unreadNotifications) => this.onUnreadNotificationsChanged(unreadNotifications), (error) => this.errorHandler.handleError(error)));
        })));
        this.onMarkAllAsRead = this.effect((_$) => combineLatest([_$, this.userId$]).pipe(switchMap$1(([_, userId]) => {
            this.setLoading();
            return from(this.userAppNotificationService.markAllAsRead(userId)).pipe(tapResponse((_) => this.onMarkedAllAsRead(), (error) => this.errorHandler.handleError(error)));
        })));
        this.onClearAll = this.effect((_$) => combineLatest([_$, this.userId$]).pipe(switchMap$1(([_, userId]) => {
            this.setLoading();
            return from(this.userAppNotificationService.deleteAllNotifications(userId)).pipe(tapResponse((_) => this.onAllCleared(), (error) => this.errorHandler.handleError(error)));
        })));
        this.onClick = this.effect((notification$) => notification$.pipe(switchMap$1((notification) => {
            this.notificationHandler.onClick(notification);
            return from(this.userAppNotificationService.markNotificationAsRead(notification.id)).pipe(tapResponse((_) => this.onClicked(notification), (error) => this.errorHandler.handleError(error)));
        })));
        this.onDelete = this.effect((notificationId$) => notificationId$.pipe(switchMap$1((notificationId) => {
            return from(this.userAppNotificationService.deleteNotification(notificationId)).pipe(tapResponse((_) => this.onDeleted(notificationId), (error) => this.errorHandler.handleError(error)));
        })));
        // UPDATERS
        this.onUnreadNotificationsChanged = this.updater((state, notifications) => ({
            ...state,
            unreadNotifications: notifications,
            loaded: true,
        }));
        this.onNotificationsChanged = this.updater((state, notifications) => ({
            ...state,
            notifications,
            loaded: true,
            fetchedAll: this.infiniteScroll.done,
        }));
        this.onNextPageFetched = this.updater((state, notifications) => ({
            ...state,
            notifications: state.notifications.concat(notifications ?? []),
            loaded: true,
        }));
        this.setLoading = this.updater((state) => ({
            ...state,
            loaded: false,
        }));
        this.setFetchedAll = this.updater((state, fetchedAll) => ({
            ...state,
            fetchedAll,
        }));
        this.onDeleted = this.updater((state, notificationId) => ({
            ...state,
            deleted: state.deleted.add(notificationId),
        }));
        this.onClicked = this.updater((state, notification) => ({
            ...state,
            viewed: state.viewed.set(notification.id, true),
        }));
        this.onMarkedAllAsRead = this.updater((state) => ({
            ...state,
            viewed: state.notifications.reduce((previous, current) => {
                return previous.set(current.id, true);
            }, state.viewed),
            loaded: true,
        }));
        this.onAllCleared = this.updater((state) => ({
            ...state,
            deleted: state.notifications.reduce((previous, current) => {
                return previous.add(current.id);
            }, state.deleted),
            loaded: true,
        }));
        this.userId$.subscribe((userId) => {
            this.infiniteScroll = this.userAppNotificationService.getInfiniteScroll(userId, DEFAULT_PAGE_SIZE);
            this.onListenNotifications();
            this.onListenUnreadNotificationsLimit(userId);
            // this.onGetNotificationsPage()
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationStore, deps: [{ token: BaseNotificationConfigProvider }, { token: UserAppNotificationService }, { token: NotificationHandlerService }, { token: i4$1.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BaseNotificationConfigProvider }, { type: UserAppNotificationService }, { type: NotificationHandlerService }, { type: i4$1.E11ErrorHandlerService }] });
const notificationToVMErrorFn = (notification) => ({
    type: ERROR_TYPES.NOTIFICATION_TO_VM,
    title: 'Error viewing notification',
    message: `Error converting notification to view model`,
    additionalData: { notification },
});

class UiNotificationLongComponent {
    constructor(store, viewportService) {
        this.store = store;
        this.viewportService = viewportService;
        // @Input() isMobile!: boolean
        this.hasFetchedAll$ = this.store.hasFetchedAll;
        this.notifications$ = this.store.getNotificationVMs;
        this.loaded$ = this.store.isLoaded;
        this.isMobile$ = this.viewportService.viewportSizeChanged$.pipe(map$1((data) => data == 'sm' || data == 'xs'), filter$1(isNotNil));
    }
    async showMore() {
        this.store.onGetNotificationsPage();
    }
    notificationClicked(notification) {
        this.store.onClick(notification.originalNotification);
    }
    clearSingleNotification(notification) {
        this.store.onDelete(notification.originalNotification.id);
    }
    markAllAsRead() {
        this.store.onMarkAllAsRead();
    }
    clearAll() {
        this.store.onClearAll();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationLongComponent, deps: [{ token: NotificationStore }, { token: i2$2.ViewportService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiNotificationLongComponent, selector: "ui-notification-long", ngImport: i0, template: `
    <ng-container *ngrxLet="notifications$ as notifications">
      <ng-container *ngrxLet="loaded$ as loaded">
        <ng-container *ngrxLet="isMobile$ as isMobile">
          <ng-container *ngrxLet="hasFetchedAll$ as hasFetchedAll">
            <div class="e11-flex e11-w-full e11-mb-4">
              <div class="e11-grow e11-truncate">
                <h1 *ngIf="!isMobile" class="e11-text-skin-primary-accent e11-mb-0 e11-truncate">{{ 'Notifications' | translate }}</h1>
                <h3 *ngIf="isMobile" class="e11-text-skin-primary-accent e11-mb-0 e11-truncate">{{ 'Notifications' | translate }}</h3>
              </div>
              <div class="e11-flex e11-justify-end">
                <e11-button
                  (click)="markAllAsRead()"
                  [size]="isMobile ? 'sm' : 'md'"
                  [color]="'primary'"
                  [ghost]="true"
                  value="{{ 'Mark all as read' | translate }}"
                  [buttonGroup]="true"
                >
                </e11-button>
                <e11-button (click)="clearAll()" [size]="isMobile ? 'sm' : 'md'" [color]="'secondary'" value="{{ 'Clear all' | translate }}">
                </e11-button>
              </div>
            </div>
            <div class="e11-bg-white e11-shadow e11-overflow-hidden sm:e11-rounded-md">
              <ul role="list" class="e11-divide-y e11-divide-gray-200">
                <ui-notification-long-card
                  *ngFor="let notification of notifications; let i = index"
                  [border]="i != notifications.length - 1"
                  [notification]="notification"
                  (cardClicked)="notificationClicked(notification)"
                  (xClicked)="clearSingleNotification(notification)"
                ></ui-notification-long-card>
              </ul>
              <div *ngIf="loaded && notifications.length === 0" class="e11-text-center e11-py-6">
                <svg class="e11-mx-auto e11-h-12 e11-w-12 e11-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <h3 class="e11-mt-2 e11-text-sm e11-font-medium e11-text-gray-900 e11-mb-0">{{ 'Nothing yet' | translate }}</h3>
                <p class="e11-mt-1 e11-text-sm e11-text-gray-500 e11-mb-0">{{ 'This is where you get important notifications' | translate }}</p>
              </div>
            </div>
            <div class="e11-w-full e11-flex e11-justify-center" *ngIf="loaded && !hasFetchedAll">
              <e11-button class="e11-mt-4" [value]="'Show More' | translate" (click)="showMore()"></e11-button>
            </div>
            <e11-loader-dots *ngIf="!loaded" [size]="'md'" [color]="'e11-bg-skin-primary-accent'"></e11-loader-dots>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
  `, isInline: true, styles: ["@media screen and (max-width: 640px){div[role=menu]{position:fixed;left:1vw;width:98vw}}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "component", type: i8.E11LoaderDotsComponent, selector: "e11-loader-dots", inputs: ["color", "size", "fontSize", "fontColor", "label", "buttonLoader", "fade", "fullScreen", "active", "hasBackdrop", "backdropStyle"] }, { kind: "directive", type: i11.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: UiNotificationLongCardComponent, selector: "ui-notification-long-card", inputs: ["notification", "border"], outputs: ["cardClicked", "xClicked"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationLongComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ui-notification-long', template: `
    <ng-container *ngrxLet="notifications$ as notifications">
      <ng-container *ngrxLet="loaded$ as loaded">
        <ng-container *ngrxLet="isMobile$ as isMobile">
          <ng-container *ngrxLet="hasFetchedAll$ as hasFetchedAll">
            <div class="e11-flex e11-w-full e11-mb-4">
              <div class="e11-grow e11-truncate">
                <h1 *ngIf="!isMobile" class="e11-text-skin-primary-accent e11-mb-0 e11-truncate">{{ 'Notifications' | translate }}</h1>
                <h3 *ngIf="isMobile" class="e11-text-skin-primary-accent e11-mb-0 e11-truncate">{{ 'Notifications' | translate }}</h3>
              </div>
              <div class="e11-flex e11-justify-end">
                <e11-button
                  (click)="markAllAsRead()"
                  [size]="isMobile ? 'sm' : 'md'"
                  [color]="'primary'"
                  [ghost]="true"
                  value="{{ 'Mark all as read' | translate }}"
                  [buttonGroup]="true"
                >
                </e11-button>
                <e11-button (click)="clearAll()" [size]="isMobile ? 'sm' : 'md'" [color]="'secondary'" value="{{ 'Clear all' | translate }}">
                </e11-button>
              </div>
            </div>
            <div class="e11-bg-white e11-shadow e11-overflow-hidden sm:e11-rounded-md">
              <ul role="list" class="e11-divide-y e11-divide-gray-200">
                <ui-notification-long-card
                  *ngFor="let notification of notifications; let i = index"
                  [border]="i != notifications.length - 1"
                  [notification]="notification"
                  (cardClicked)="notificationClicked(notification)"
                  (xClicked)="clearSingleNotification(notification)"
                ></ui-notification-long-card>
              </ul>
              <div *ngIf="loaded && notifications.length === 0" class="e11-text-center e11-py-6">
                <svg class="e11-mx-auto e11-h-12 e11-w-12 e11-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <h3 class="e11-mt-2 e11-text-sm e11-font-medium e11-text-gray-900 e11-mb-0">{{ 'Nothing yet' | translate }}</h3>
                <p class="e11-mt-1 e11-text-sm e11-text-gray-500 e11-mb-0">{{ 'This is where you get important notifications' | translate }}</p>
              </div>
            </div>
            <div class="e11-w-full e11-flex e11-justify-center" *ngIf="loaded && !hasFetchedAll">
              <e11-button class="e11-mt-4" [value]="'Show More' | translate" (click)="showMore()"></e11-button>
            </div>
            <e11-loader-dots *ngIf="!loaded" [size]="'md'" [color]="'e11-bg-skin-primary-accent'"></e11-loader-dots>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
  `, encapsulation: ViewEncapsulation.None, styles: ["@media screen and (max-width: 640px){div[role=menu]{position:fixed;left:1vw;width:98vw}}\n"] }]
        }], ctorParameters: () => [{ type: NotificationStore }, { type: i2$2.ViewportService }] });

class UiNotificationComponent {
    constructor(notificationStore, elementRef) {
        this.notificationStore = notificationStore;
        this.elementRef = elementRef;
        this.seeAllRoute = '/notification';
        this.darkTheme = false;
        this.unreadCount$ = this.notificationStore.getUnreadNotificationCount;
        this.notifications$ = this.notificationStore.getLatestNotificationVMs(NOTIFICATIONS_DROPDOWN_COUNT);
        this.UNREAD_LIMIT = UNREAD_NOTIFICATIONS_LIMIT;
        this.notificationsOpen = false;
    }
    // Closers - Coffee is for closers
    onClick(targetElement) {
        const clickedInside = this.elementRef.nativeElement.contains(targetElement);
        if (!clickedInside) {
            this.notificationsOpen = false;
        }
    }
    onKeydownHandler(event) {
        if (event.key === 'Escape') {
            this.notificationsOpen = false;
        }
    }
    navigateTo(notification) {
        this.notificationStore.onClick(notification.originalNotification);
    }
    onClickBell() {
        this.notificationsOpen = !this.notificationsOpen;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationComponent, deps: [{ token: NotificationStore }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: UiNotificationComponent, selector: "ui-notification", inputs: { seeAllRoute: "seeAllRoute", darkTheme: "darkTheme" }, host: { listeners: { "document:click": "onClick($event.target)", "document:keydown": "onKeydownHandler($event)" } }, ngImport: i0, template: `
    <ng-container *ngrxLet="unreadCount$ as unreadCount">
      <ng-container *ngrxLet="notifications$ as notifications">
        <div class="e11-ml-3 e11-relative ">
          <div class="">
            <button
              type="button"
              (click)="onClickBell()"
              class="e11-flex-shrink-0 e11-p-1  e11-rounded-full  hover:e11-bg-opacity-10 focus:e11-outline-none focus:e11-ring-2 "
              [ngClass]="{
                'e11-text-gray-300 hover:e11-text-white hover:e11-bg-white focus:e11-ring-white': !darkTheme,
                'e11-text-skin-primary-accent hover:e11-text-skin-primary focus:e11-ring-skin-primary': darkTheme
              }"
            >
              <span class="e11-sr-only">{{ 'View notifications' | translate }}</span>
              <svg
                class="e11-h-6 e11-w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <e11-badge
                *ngIf="unreadCount > 0"
                [value]="unreadCount"
                [color]="'error'"
                [size]="'sm'"
                [posAdjustX]="'-12px'"
                [posAdjustY]="'-14px'"
                [limit]="UNREAD_LIMIT"
                [containerClassOverrides]="''"
              ></e11-badge>
            </button>
          </div>
          <div
            [ngClass]="{
              'e11-transition e11-ease-out e11-duration-200 e11-transform e11-opacity-0 e11-scale-95 e11-invisible': !notificationsOpen,
              'e11-transition e11-ease-in duration-75 e11-e11-transform e11-opacity-100 e11-scale-100': notificationsOpen
            }"
            class="e11-origin-top-right e11-absolute e11-left-[-75px] sm:e11-left-auto e11-right-0 e11-mt-2 e11-w-80 e11-rounded-md e11-shadow-lg  e11-bg-white e11-ring-1 e11-ring-black e11-ring-opacity-5 focus:e11-outline-none focus:e11-ring-opacity-100 focus:e11-ring-gray-200"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu-button"
            tabindex="-1"
          >
            <ul role="list" class=" e11-overflow-y-auto e11-max-h-[75vh]">
              <ui-notification-card *ngFor="let notification of notifications" [notification]="notification" (click)="navigateTo(notification)">
              </ui-notification-card>
              <li *ngIf="!notifications.length" class="e11-py-4 e11-px-4 e11-rounded-md">
                <div class="e11-flex e11-space-x-3 ">
                  <div class="e11-flex-1 e11-space-y-1">
                    <svg
                      class="e11-h-6 e11-w-6 e11-m-auto"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    <h3 class="e11-mt-2 e11-text-sm e11-font-medium e11-text-gray-900 e11-mb-0 e11-text-center">{{ 'Nothing yet' | translate }}</h3>
                    <p class="e11-mt-1 e11-text-sm e11-text-gray-500 e11-mb-0 e11-text-center">
                      {{ 'This is where you get important notifications' | translate }}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
            <a
              *ngIf="notifications.length"
              [routerLink]="[seeAllRoute]"
              (click)="notificationsOpen = false"
              class="e11-cursor-pointer e11-block e11-bg-skin-primary-accent e11-text-skin-white e11-rounded-b-md e11-text-center e11-py-2 hover:e11-bg-skin-primary hover:e11-text-white"
              >{{ 'See all notifications' | translate }}</a
            >
          </div>
        </div>
      </ng-container>
    </ng-container>
  `, isInline: true, styles: ["@media screen and (max-width: 640px){div[role=menu]{position:fixed;left:1vw;width:98vw}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3$3.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: i4$2.E11BadgeComponent, selector: "e11-badge", inputs: ["color", "shape", "size", "inTag", "classOverrides", "class", "posAdjustY", "posAdjustX", "containerClassOverrides", "containerClass", "inline", "titleText", "titleTextPosition", "value", "limit"] }, { kind: "directive", type: i11.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: UiNotificationCardComponent, selector: "ui-notification-card", inputs: ["notification"] }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiNotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ui-notification', template: `
    <ng-container *ngrxLet="unreadCount$ as unreadCount">
      <ng-container *ngrxLet="notifications$ as notifications">
        <div class="e11-ml-3 e11-relative ">
          <div class="">
            <button
              type="button"
              (click)="onClickBell()"
              class="e11-flex-shrink-0 e11-p-1  e11-rounded-full  hover:e11-bg-opacity-10 focus:e11-outline-none focus:e11-ring-2 "
              [ngClass]="{
                'e11-text-gray-300 hover:e11-text-white hover:e11-bg-white focus:e11-ring-white': !darkTheme,
                'e11-text-skin-primary-accent hover:e11-text-skin-primary focus:e11-ring-skin-primary': darkTheme
              }"
            >
              <span class="e11-sr-only">{{ 'View notifications' | translate }}</span>
              <svg
                class="e11-h-6 e11-w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <e11-badge
                *ngIf="unreadCount > 0"
                [value]="unreadCount"
                [color]="'error'"
                [size]="'sm'"
                [posAdjustX]="'-12px'"
                [posAdjustY]="'-14px'"
                [limit]="UNREAD_LIMIT"
                [containerClassOverrides]="''"
              ></e11-badge>
            </button>
          </div>
          <div
            [ngClass]="{
              'e11-transition e11-ease-out e11-duration-200 e11-transform e11-opacity-0 e11-scale-95 e11-invisible': !notificationsOpen,
              'e11-transition e11-ease-in duration-75 e11-e11-transform e11-opacity-100 e11-scale-100': notificationsOpen
            }"
            class="e11-origin-top-right e11-absolute e11-left-[-75px] sm:e11-left-auto e11-right-0 e11-mt-2 e11-w-80 e11-rounded-md e11-shadow-lg  e11-bg-white e11-ring-1 e11-ring-black e11-ring-opacity-5 focus:e11-outline-none focus:e11-ring-opacity-100 focus:e11-ring-gray-200"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="user-menu-button"
            tabindex="-1"
          >
            <ul role="list" class=" e11-overflow-y-auto e11-max-h-[75vh]">
              <ui-notification-card *ngFor="let notification of notifications" [notification]="notification" (click)="navigateTo(notification)">
              </ui-notification-card>
              <li *ngIf="!notifications.length" class="e11-py-4 e11-px-4 e11-rounded-md">
                <div class="e11-flex e11-space-x-3 ">
                  <div class="e11-flex-1 e11-space-y-1">
                    <svg
                      class="e11-h-6 e11-w-6 e11-m-auto"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    <h3 class="e11-mt-2 e11-text-sm e11-font-medium e11-text-gray-900 e11-mb-0 e11-text-center">{{ 'Nothing yet' | translate }}</h3>
                    <p class="e11-mt-1 e11-text-sm e11-text-gray-500 e11-mb-0 e11-text-center">
                      {{ 'This is where you get important notifications' | translate }}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
            <a
              *ngIf="notifications.length"
              [routerLink]="[seeAllRoute]"
              (click)="notificationsOpen = false"
              class="e11-cursor-pointer e11-block e11-bg-skin-primary-accent e11-text-skin-white e11-rounded-b-md e11-text-center e11-py-2 hover:e11-bg-skin-primary hover:e11-text-white"
              >{{ 'See all notifications' | translate }}</a
            >
          </div>
        </div>
      </ng-container>
    </ng-container>
  `, encapsulation: ViewEncapsulation.None, styles: ["@media screen and (max-width: 640px){div[role=menu]{position:fixed;left:1vw;width:98vw}}\n"] }]
        }], ctorParameters: () => [{ type: NotificationStore }, { type: i0.ElementRef }], propDecorators: { seeAllRoute: [{
                type: Input
            }], darkTheme: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }] } });

class NotificationsComponent {
    constructor() { }
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: NotificationsComponent, selector: "lib-notifications", ngImport: i0, template: ` <p>notifications works!</p> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-notifications', template: ` <p>notifications works!</p> ` }]
        }], ctorParameters: () => [] });

const ImportExportModules = [
    // New E11 Modules
    E11BadgeModule,
    E11ButtonModule,
    E11LoaderDotsModule,
    E11PanelModule,
    E11ModalModule,
    E11TextareaModule,
    E11InputModule,
    E11DynamicFormModule,
    E11NotificationsModule,
    E11SelectModule,
];
class UiComponentLibraryModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, imports: [CommonModule, 
            // New E11 Modules
            E11BadgeModule,
            E11ButtonModule,
            E11LoaderDotsModule,
            E11PanelModule,
            E11ModalModule,
            E11TextareaModule,
            E11InputModule,
            E11DynamicFormModule,
            E11NotificationsModule,
            E11SelectModule], exports: [
            // New E11 Modules
            E11BadgeModule,
            E11ButtonModule,
            E11LoaderDotsModule,
            E11PanelModule,
            E11ModalModule,
            E11TextareaModule,
            E11InputModule,
            E11DynamicFormModule,
            E11NotificationsModule,
            E11SelectModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, imports: [CommonModule, ImportExportModules, 
            // New E11 Modules
            E11BadgeModule,
            E11ButtonModule,
            E11LoaderDotsModule,
            E11PanelModule,
            E11ModalModule,
            E11TextareaModule,
            E11InputModule,
            E11DynamicFormModule,
            E11NotificationsModule,
            E11SelectModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UiComponentLibraryModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, ...ImportExportModules],
                    exports: [...ImportExportModules],
                }]
        }] });

const DECLARE_EXPORT_COMPONENTS = [
    NotificationsComponent,
    HtmlEditorComponent,
    ListTemplatesComponent,
    EditTemplateComponent,
    ListNotificationsComponent,
    EditNotificationComponent,
];
class NotificationsEditorModule {
    static forRoot(rootConfig) {
        return {
            ngModule: NotificationsEditorModule,
            providers: [
                {
                    provide: BaseNotificationConfigProvider,
                    useClass: rootConfig.configProvider,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationsEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: NotificationsEditorModule, declarations: [NotificationsComponent,
            HtmlEditorComponent,
            ListTemplatesComponent,
            EditTemplateComponent,
            ListNotificationsComponent,
            EditNotificationComponent, NotificationTestComponent, UiNotificationConfigComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            UiComponentLibraryModule,
            EmailEditorModule,
            TranslateModule,
            LetDirective,
            SearchModule,
            E11ConfirmationModule,
            WebUtilityModule], exports: [NotificationsComponent,
            HtmlEditorComponent,
            ListTemplatesComponent,
            EditTemplateComponent,
            ListNotificationsComponent,
            EditNotificationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationsEditorModule, imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            UiComponentLibraryModule,
            EmailEditorModule,
            TranslateModule,
            SearchModule,
            E11ConfirmationModule,
            WebUtilityModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationsEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...DECLARE_EXPORT_COMPONENTS, NotificationTestComponent, UiNotificationConfigComponent],
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        UiComponentLibraryModule,
                        EmailEditorModule,
                        TranslateModule,
                        LetDirective,
                        SearchModule,
                        E11ConfirmationModule,
                        WebUtilityModule,
                    ],
                    exports: [...DECLARE_EXPORT_COMPONENTS],
                }]
        }] });
class NotificationHandlerModule {
    static forRoot(options = { notificationHandlers: [], configProvider: DefaultLibConfiguration }) {
        return {
            ngModule: NotificationHandlerModule,
            providers: [
                options.notificationHandlers.map(transform),
                {
                    provide: BaseNotificationConfigProvider,
                    useClass: options.configProvider ?? DefaultLibConfiguration,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerModule, declarations: [UiNotificationCardComponent, UiNotificationComponent, UiNotificationLongCardComponent, UiNotificationLongComponent], imports: [CommonModule, RouterModule, FormsModule, UiComponentLibraryModule, TranslateModule, LetDirective], exports: [UiNotificationCardComponent, UiNotificationComponent, UiNotificationLongCardComponent, UiNotificationLongComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerModule, imports: [CommonModule, RouterModule, FormsModule, UiComponentLibraryModule, TranslateModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: NotificationHandlerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [UiNotificationCardComponent, UiNotificationComponent, UiNotificationLongCardComponent, UiNotificationLongComponent],
                    imports: [CommonModule, RouterModule, FormsModule, UiComponentLibraryModule, TranslateModule, LetDirective],
                    exports: [UiNotificationCardComponent, UiNotificationComponent, UiNotificationLongCardComponent, UiNotificationLongComponent],
                }]
        }] });
const transform = (notificationHandler) => ({ multi: true, provide: APP_NOTIFICATION_HANDLER_TOKEN, useClass: notificationHandler });

/*
 * Public API Surface of notifications
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseNotificationConfigProvider, DefaultLibConfiguration, EditNotificationComponent, EditTemplateComponent, EmailNotificationReplaceTokens, HtmlEditorComponent, ListNotificationsComponent, ListTemplatesComponent, NotificationConfigStore, NotificationHandlerModule, NotificationHandlerService, NotificationPermissions, NotificationStore, NotificationsComponent, NotificationsEditorModule, UiNotificationCardComponent, UiNotificationComponent, UiNotificationLongCardComponent, UiNotificationLongComponent, UserAppNotificationService, notificationAdapter };
